import { useCallback, useRef, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import store from "../State/Store/store";
import StorageManager from "../utils/StorageManager";
import TickerTimeout from "../utils/TickerTimeout";
import {
  setWinAnimationsPlaying,
  setResetMainGameVisualEffects,
  setCounterAnimationStarted,
  setCounterAnimationFinished,
  setCriticalAnimationPlaying,
  setExpandingWildsOccurred,
  setSkipWinAnimation,
  setUserWantsToDismissWinAnimation,
  setSkipCounterAnimation,
  setIsWaitingForUserConfirmation,
  closeAllPopups,
  setSymbolPopupBlocked,
  openTurboSuggestionPopup,
  setTurboSuggestionPending,

  setIsAnimatedWin,
  setUserInterfaceDisabled,
  setShowFreeSpinIntro,
  setShowGambleDialog,
  setShowRoyalCollectIntro,
  setShowPotCollectionIntro,
  setShowHoldAndWinIntro,
  setFreeSpinsTier,
  commitFreeSpinsProgress,
  commitFreeSpinsRoomLevel,
  commitFreeSpinsServerLevel,
  announceFreeSpins,
  setShowAdditionalFreeSpins,
} from "../State/actions";
import {
  selectResetMainGameVisualEffects,
  selectFreeSpinsCurrentRound,
  selectFreeSpinsPlayed,
  selectFreeSpinsWinningLines,

  selectFreeSpinsSpinComplete,
  selectFreeSpinsSpinning,
  selectTurboSuggestionPending,
  selectFreeSpinActive,

  selectAutoPlayActive,
  selectIntroPopupActive,
  selectIntroTransitionActive,
  selectFreeSpinsReloadPopupPending,
  selectInfoPopupActive,
  selectWinAnimationsPlaying,
  selectCounterAnimationFinished,
  selectCriticalAnimationPlaying,

  selectFreeSpinsPendingTier,
  selectFreeSpinsPendingServerLevel,
  selectFreeSpinsPendingLevelUpSpins,
  selectFreeSpinsPendingLevelUpSpinsList,
  selectFreeSpinsTier,
  selectFreeSpinsTriggered,
  selectFreeSpinsActive,
  selectRoyalCollectTriggered,
  selectPotCollectionTriggered,
  selectReplaySessionActive,
} from "../State/selectors";
import {
  multiplierForServerLevel,
  FREE_SPINS_TIERS_PER_LEVEL,
} from "../State/Slices/freeSpinsSlice";
import { selectHoldAndWinIntroPending } from "../State/Slices/holdAndWinSlice";
import { playTriggerCoinWait } from "../Games/Shared/triggerCoinWait";
import { isCoinSymbol } from "../SymbolData";
import { SOUND_NAME } from "../sounds";

import { useReelRefs } from "../Contexts/ReelRefsContext";
import { useReelControl } from "../Contexts/ReelControlContext";
import { GAME_LAYOUT, Settings } from "../settings";
import { WIN_TEXT_CONFIG } from "../Components/Animations/WinText/WinTextController";
import {
  WILD_SYMBOL_ID,
  MYSTERY_SYMBOL_ID,
  BONUS_SYMBOL_ID,
  SCATTER_1_SYMBOL_ID,
  SCATTER_3_SYMBOL_ID,
  symbolData,
} from "../SymbolData";

import { mysteryTargetsByReel } from "../Games/Shared/mysteryStaging";
import { mysteryDrama, MYSTERY_DRAMA_CLIPS } from "../Games/Shared/mysteryDrama";
import {
  goldenCellTexture,
  goldenWinSound,
  plusSkinForCount,
} from "../Games/Main/Mystery/goldenMysteryConfig";
import {
  goldenOutcomeAt,
  goldenStopOrder,
  goldenShownBoard,
  goldenPlusCells,
  goldenLevelCells,
  goldenPayingCells,
} from "../Games/Shared/goldenMysteryStaging";
import { runGoldenMysteryLoop } from "../Games/Shared/goldenMysteryRunner";
import { isFreeSpinsCapReached } from "../Games/Shared/freeSpinOutroPopup";
import {
  SCENE_ACCENTS,
  SCENE_ACCENT_TRACKS,
} from "../Games/Shared/sceneAccents";
import {
  selectPaytable,
  selectBaseBet,
  selectBet,
} from "../State/selectors";
import {
  FEATURES_CONFIG,
  playCascadingExpandingWild,
  playFullReelExpandingWild,
  winLoops,
} from "../featuresConfig";
import { boxedReelsOnLine } from "../Games/Shared/WinLinesReelSet/WinLinesController";
import { soundManager } from "../SoundManager";
import SpineAnimationData from "../utils/SpineAnimationData";
import { isTurboLike } from "../utils/speedHelpers";
import { fsDiag, fsBlockedBy } from "../utils/fsDiag";
import { waitForBoardToSettle } from "../Games/Shared/waitForBoardToSettle";

// Cleopatra's Fire retrigger: the pause between the spin's win finishing and
// the Additional Free Spins popup (see the retrigger branch). Tune here.
// Dropped — the popup waits on the scatter win's own clip instead (Vladimir,
// 2026-09-29: "use animation timing ... no timeouts").
// const RETRIGGER_POPUP_DELAY_MS = 1000;

/**
 * Which symbols charge a level box.
 *
 * SC (id 11) is what the server actually drops. GDD I14 says the PLUS symbols
 * replace SC/SC1 inside the bonus game, and this list was built on that — it
 * found nothing, ever, because a live free-spins response reads:
 *
 *   "special_symbols": { "scatter": { "11": [[2,5]] } }
 *
 * and `plus_fs_scatters` is empty in every capture we have. `scatters_to_collect`
 * — the field the ladder is counted from — is that same data restated, so SC is
 * the symbol doing the charging. See docs/api-contract-mad-labs-freespins.md.
 *
 * PLUS (16) stays in the list for when the Coins feature ships and starts
 * dropping them; both charge a box, so both belong here.
 */
const LEVEL_CHARGING_SYMBOL_IDS = [SCATTER_1_SYMBOL_ID, SCATTER_3_SYMBOL_ID];

/**
 * "This spin has not been presented yet."
 *
 * A free-spin round carries the SAME id for every spin in the bonus — the
 * round id, not the spin's — so a once-per-round guard that stores it matches
 * for ever after the first one. That is what made the mystery play on its
 * first appearance and never again.
 *
 * A Symbol can never equal a round id, so the guards start and re-arm on this
 * instead. What marks a new spin is the spin itself, not the payload.
 */
const UNPRESENTED = Symbol("unpresented");

/**
 * Frames the mystery reveal will wait for the room's reels to be built before
 * giving up. ~2 seconds at 60fps — far longer than a reload takes, short enough
 * that a room which never arrives does not bump state for ever.
 */
const MYSTERY_SCENE_MAX_WAITS = 120;


/**
 * Every cell on the landed board that charges a level box.
 *
 * Reads the ROUND rather than the reel controllers: the round is what the
 * server said landed, and it is already the post-mystery board.
 *
 * @param   {object} round
 * @returns {Array<{reelIndex:number,row:number,symbolId:number}>}
 */
const scatterCellsOnBoard = (round) => {
  const reels = round?.reels;
  if (!Array.isArray(reels)) return [];

  const cells = [];
  reels.forEach((reel, reelIndex) => {
    if (!Array.isArray(reel)) return;
    reel.forEach((symbolId, row) => {
      if (LEVEL_CHARGING_SYMBOL_IDS.includes(symbolId)) {
        cells.push({ reelIndex, row, symbolId });
      }
    });
  });
  return cells;
};

/**
 * The cells that will FIRE A BOLT at a level box on this round.
 *
 * Two callers, and they must never disagree: the level-charge walk fires
 * exactly this list, and the win presentation has to know in advance whether
 * that walk is coming so it can get out of its way. Written once here rather
 * than twice, because the day they drift the lines cycle under the bolts again
 * and it will look like a timing bug rather than a duplicated rule.
 *
 * `pendingTier === null` gates the WHOLE list: at the ceiling rung nothing
 * advances, so there is no pip to fill and a bolt would promise progress that
 * cannot happen. Spins may still be announced; the ladder simply does not move.
 *
 * @param   {object} round        the free-spin board
 * @param   {number|null} pendingTier  freeSpins.pendingTier
 * @returns {Array<object>} the walk's own cell list, possibly empty
 */
const boxChargeCells = (round, pendingTier) =>
  pendingTier === null
    ? []
    : [
        ...scatterCellsOnBoard(round),
        ...goldenLevelCells(round).map((c) => ({
          reelIndex: c.reel,
          row: c.row,
          symbolId: c.symbolId,
          fsCount: c.fsCount,
          // Fired through the Golden cell that owns it, not the overlay's
          // ordinary clip path — see the branch in the walk.
          golden: true,
        })),
      ];

// ── This board is auto-DRIVEN, which is not the same as turbo ──
//
// The base game asks settings.autoPlayActive two different questions with one
// flag:
//
//   1. TIMING — hold the global win for the full display window (2 loops)
//      instead of the flat manual SPIN_UNBLOCK_DELAY, because the machine, not
//      a person, decides when the next spin starts.
//   2. SPEED — skip per-line cycling entirely, because the player asked to go
//      faster (turboActiveRef is `spacebarTurbo || autoPlayActive`).
//
// Nothing sets settings.autoPlayActive for the bonus — its spins come from
// FreeSpinBasicControlContext's own loop — so question 1 was answered wrong and
// the 2-loop hold collapsed to 1000ms.
//
// This constant answers question 1 ONLY. Answering question 2 with it was a
// mistake and it broke the round: with cycling skipped, a round whose only wins
// are coins takes the trigger-only path, skips the global show-all as well, and
// then NOTHING dispatches counterAnimationFinished — the release lives inside
// the two things that were skipped. The loop waits on that flag for a paying
// round, so the round stopped dead with no win text. Big wins lost their
// backing lines the same way.
//
// A free spin is not a speed request. turboActiveRef keeps reading the real
// setting, exactly as the base game does.
const BONUS_AUTO_DRIVEN = true;

// Hook for managing win line animations, sound effects, and reel coordination
/**
 * Win presentation for the FREE SPIN board.
 *
 * A copy of useWinController pointed at the bonus room: its reel refs, its
 * overlay refs, its dim / win-box / win-text instances, and its own round.
 * Copied rather than parameterised for the same reason the board itself was —
 * the base game's win display is the most-watched thing in the game and must
 * not be put at risk by bonus work.
 *
 * ── KEEP THIS FILE A PURE COPY ──
 *
 * It is regenerated from useWinController by applying exactly one transform:
 * the four selectors below, the ref destructure (including
 * freeSpinBasicSnapClearAllRef — this board must NOT share the base game's, or
 * whichever hook renders last owns it), BONUS_AUTO_DRIVEN in place of the ONE
 * settings.autoPlayActive read that decides the global win's hold time, and
 * this hook's own name. The other two reads of that flag are speed decisions
 * and keep the player's real setting — see the constant.
 * NOTHING else may differ. Two near-identical 2,500-line files drift silently,
 * and that drift has already cost real time — the bonus board was left without
 * the reveal's static-texture swap and without the dim gating, so a fix proven
 * in the base game simply did not exist here.
 *
 * So: fix it in useWinController FIRST, then re-apply the transform. Verify
 * with `diff` — the only hunks should be the ones this docblock names.
 *
 * The OLD useFreeSpinWinController is Zeus's and is not this: it drives the
 * mask-reveal board off the singular featureGame flags, and has drifted a long
 * way from the current win controller.
 */
export const useFreeSpinBasicWinController = () => {
  const {
    freeSpinBasicReelRefs: reelRefs,
    freeSpinBasicOverlayReelRefs: overlayReelRefs,
    // NOT renamed, and there is no bonus equivalent to rename it to.
    // WIN_LINES.ENABLED is false in this game, so nothing reads this ref on
    // either board — win BOXES are the feature that ships, and the bonus board
    // has its own. If win lines are ever turned on, this needs a
    // freeSpinBasicWinLinesContainerRef or the bonus will draw its lines on the
    // hidden base board.
    // winLinesContainerRef,
    // Win lines are on (2026-10-13): the bonus board's own.
    freeSpinBasicWinLinesContainerRef: winLinesContainerRef,
    freeSpinBasicWinBoxContainerRef: winBoxContainerRef,
    freeSpinBasicDimReelSetRef: dimReelSetRef,
    freeSpinBasicGlobalLineWinTextRef: globalLineWinTextRef,
    freeSpinBasicLineWinTextRef: lineWinTextRef,
    characterAnimationControllerRef,
    potChargingAnimatorRef,
    levelChargingAnimatorRef,
    freeSpinsGameViewControllerRef,
    additionalFreeSpinsControllerRef,
    freeSpinBasicSnapClearAllRef: snapClearAllRef,
  } = useReelRefs();

  const { spacebarTurboActive, spacebarTurboActiveRef } = useReelControl();

  const dispatch = useDispatch();

  // This feature's own flag, not spin.spinComplete. The bonus round brackets
  // each spin with freeSpins.spinning / freeSpins.spinComplete, exactly the
  // pair the base game has in spin.isSpinning / spin.spinComplete — separate
  // sets, so neither feature can start or end the other's spin.
  const spinComplete = useSelector(selectFreeSpinsSpinComplete);
  // ── "Spinning" means THIS board, not the base one ──
  //
  // Every per-spin reset in here is keyed off "a new spin has started". In the
  // base game that is spin.isSpinning — but spinSlice deliberately routes
  // `freespin` to the featureSpin* flags so the two rounds cannot touch each
  // other's state, so spin.isSpinning never moves once the bonus is running.
  //
  // Left wired to the base flag, the reset effect below never ran:
  // animationCompletedRef stayed latched after the FIRST free spin, and from
  // the second spin on the main effect returned at its own guard — no win
  // lines, no boxes, no counter, nothing. The round looked like it had stopped
  // caring about wins, because it had.
  const isSpinning = useSelector(selectFreeSpinsSpinning);
  const currentRound = useSelector(selectFreeSpinsCurrentRound);
  const winningLines = useSelector(selectFreeSpinsWinningLines);
  // How many free spins have RESOLVED. Zero means the board on screen is the
  // copied trigger screen — see the arrival gate in the main effect.
  const freeSpinsPlayed = useSelector(selectFreeSpinsPlayed);

  const turboSuggestionPending = useSelector(selectTurboSuggestionPending);
  const freeSpinActive = useSelector(selectFreeSpinActive);
  // ── Only act while MY board is the one on screen ──
  //
  // `freeSpinActive` above is Zeus's featureGame flag and nothing in Mad Labs
  // sets it, so the two guards below were dead. That went unnoticed until the
  // base game's entry teardown started clearing its own win dedupe: with
  // animationCompletedRef re-armed and rgsPlay.fulfilled setting base
  // spinComplete on a FEATURE response (see the wasFeatureSpin branch in
  // spinSlice), the base controller re-animated the TRIGGER round on the first
  // free spin — a win appearing before the bonus reels had stopped.
  //
  // This is the Mad Labs flag, and it is the ONE line that differs by board:
  //   bonus board  bail while it does NOT — the base game's job then.
  //
  // NEGATED against useWinController. This is the one place the copy is not
  // literal, and it is listed in the transform above.
  const bonusBoardActive = !useSelector(selectFreeSpinsActive);
  const resetMainGameVisualEffects = useSelector(
    selectResetMainGameVisualEffects,
  );
  // The SPEED question — the player's own setting, untouched. See
  // BONUS_AUTO_DRIVEN above for the timing one.
  const autoPlayActive = useSelector(selectAutoPlayActive);
  const introPopupActive = useSelector(selectIntroPopupActive);
  // The intro's wipe. The popup flag drops when the screen goes black, ~400ms
  // before the room is visible; this one drops when the wipe has finished.
  const introTransitionActive = useSelector(selectIntroTransitionActive);
  const infoPopupActive = useSelector(selectInfoPopupActive);
  // The mid-round reload popup owns the screen, or is about to. Armed in
  // freeSpinsSlice off the init rather than by the scene, so it is true on the
  // FIRST frame — the scene cannot raise its own flag until the boot intro
  // flips showGameAfterTransition, and a restored win presenting in that gap is
  // exactly what put a big win on top of the reload screen.
  const freeSpinsReloadActive = useSelector(
    selectFreeSpinsReloadPopupPending,
  );
  // PLURAL — freeSpins.triggered, the Mad Labs slice. The singular
  // featureGame.freeSpinTriggered is Zeus's and is never set in this game,
  // which is why every gate below used to be dead.
  const freeSpinTriggered = useSelector(selectFreeSpinsTriggered);
  const royalCollectTriggered = useSelector(selectRoyalCollectTriggered);
  const potCollectionTriggered = useSelector(selectPotCollectionTriggered);


  // Refs for state management and animation control
  const reelsetControllerRef = useRef(null);
  const currentTimeoutsRef = useRef([]);
  const pendingAnimationFramesRef = useRef([]);
  const isAnimatingRef = useRef(false);
  const isReadyRef = useRef(false);
  const animationCompletedRef = useRef(false);
  // Tracks the round ID that was fast-pathed in super turbo.
  // The recovery effect uses this to undo the fast path if the user releases
  // spacebar within the same round (keyup fires AFTER the fast path committed).
  const fastPathRoundRef = useRef(null);
  const isFirstAnimationRef = useRef(true);
  const spinCompleteRef = useRef(spinComplete);
  const currentRoundRef = useRef(currentRound);
  // Round id whose reveal sequence has already run. The win display cycles its
  // lines repeatedly; a reveal must fire once for the round, not once a cycle.
  // Initialised UNDEFINED, not null. A live round with no id resolves to
  // null, and `null !== null` reads as "already played" — which is exactly how
  // the reveal silently never ran. Undefined can never equal a round id, so a
  // missing id now fails safe (it plays) instead of failing silent.
  const revealPlayedForRoundRef = useRef(undefined);
  // Round id whose reveal has FINISHED. Until it matches, the trigger line
  // suppresses its win animations; once it does, later passes of the cycle
  // animate normally instead of sitting silent.
  const revealDoneForRoundRef = useRef(undefined);
  // Round id whose reveal actually AWARDED something. Read off the round's own
  // reveal outcome, not off freeSpins.triggered — the award is already carried
  // on the round as gambleScatter, and the reveal config turns it into
  // `awarded`. Going through the slice meant devSimulation, which writes
  // currentRound directly and never passes through RGS, could never test the
  // win path at all.
  const revealAwardedForRoundRef = useRef(undefined);
  const winningLinesRef = useRef(winningLines);
  const winningDataRef = useRef(null);
  const lineIdsRef = useRef([]);
  const currentLineIndexRef = useRef(0);
  const dispatchRef = useRef(dispatch);

  const turboActiveRef = useRef(false);
  const infoPopupActiveRef = useRef(infoPopupActive);
  const winSoundPlayedRef = useRef(false);
  const expandingWildReelIndicesRef = useRef([]);
  const animatedRoundsRef = useRef(new Set());

  const executeOnReelControllersRef = useRef(null);
  const executeOnOverlayReelControllersRef = useRef(null);
  const currentIntervalsRef = useRef([]);
  const expandingWildAnimationStateRef = useRef({});
  const currentSpineListenerRef = useRef(null);
  const useFastWinRef = useRef(false); // Sticky flag: true if this spin entered with win_fast (persists into line cycling)
  const prevWinPosSetRef = useRef(null); // Previous line's winning positions for diff-based transitions


  // Memoize simple derived values
  const hasWins = useMemo(
    () => currentRound?.win && currentRound.win > 0,
    [currentRound?.win],
  );

  const hasScatterWin = (currentRound?.scatterCount || 0) > 0;


  const winType = currentRound?.winType || "";

  // ── The celebration, and the one case it is withheld ──
  //
  // `winAlreadyPresented` is set by gameSettingsSlice when it hands the live
  // round back at the end of a replay session. That round was celebrated before
  // the player opened the history, so playing the tier chain again is a second
  // fanfare for money paid minutes ago.
  //
  // Suppressed HERE and nowhere else, because this is the single place the flag
  // is derived and every `setWinAnimationsPlaying(true)` in this file is gated
  // on it. Everything else about the round is untouched: the win lines still
  // cycle, the win boxes still draw, the amount still shows and the balance
  // still updates (updateDisplayedBalance is a SYNC, not an add, so re-running
  // the presentation cannot double-credit). Only the big-win animation goes.
  // ── A CAPPED round has ONE fanfare, and it is MAX WIN ──
  //
  // Same suppression, same place, same reason as the replay case above.
  // Vladimir, 2026-09-22, watching a capped round play BIG -> MEGA -> SUPER
  // MEGA and then the MAX WIN popup: "the bet is already shown, the value down
  // at the win display is already max win, lets just cut the crap — skip entire
  // sequence, pop the max win popup and exit."
  //
  // He is right and the GDD agrees: Zeus §18.2 steps 6-11 describe ONE popup
  // that celebrates AND closes the bonus, and Mad Labs §11 says Max Win skips
  // the BW->MW->SMW sequence entirely. A tier chain in front of it is a second
  // fanfare for the same money.
  //
  // Everything else about the round still runs — win lines cycle, win boxes
  // draw, the amount counts up, the balance syncs — exactly as the replay
  // suppression leaves them. Only the big-win spine goes, and MAX WIN follows
  // it as the round's own ending.
  //
  // Asked of the STATE, not of this round's winType: the cap is the round
  // total against bet x max_multiplier, and a capped round can be closed by a
  // spin that paid under the tier thresholds itself.
  const freeSpinsCapReached = useSelector(isFreeSpinsCapReached);

  const isAnimatedWin =
    (currentRound?.isAnimatedWin &&
      !currentRound?.winAlreadyPresented &&
      !freeSpinsCapReached) ||
    false;



  /**
   * Clears all active timeouts, intervals, and pending animation frames.
   * Prevents memory leaks and race conditions when animations are interrupted or component unmounts.
   */
  const clearAllTimeouts = useCallback(() => {
    const timeouts = currentTimeoutsRef.current;
    for (let i = 0; i < timeouts.length; i++) {
      const t = timeouts[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearTimeout(t);
      }
    }

    currentTimeoutsRef.current = [];

    const intervals = currentIntervalsRef.current;
    for (let i = 0; i < intervals.length; i++) {
      const t = intervals[i];
      if (t && typeof t.clear === "function") {
        t.clear();
      } else {
        clearInterval(t);
      }
    }
    currentIntervalsRef.current = [];

    const animationFrames = pendingAnimationFramesRef.current;
    for (let i = 0; i < animationFrames.length; i++) {
      cancelAnimationFrame(animationFrames[i]);
    }
    pendingAnimationFramesRef.current = [];

    // Remove any pending spine complete listener to prevent stale resolves
    if (currentSpineListenerRef.current) {
      const { spineInstance, listener } = currentSpineListenerRef.current;
      try {
        spineInstance?.state?.removeListener(listener);
      } catch {
        // spine may already be destroyed
      }
      currentSpineListenerRef.current = null;
    }
  }, []);

  // ── Leaving a replay: hand the presentation refs back ──
  //
  // The twin of the effect in useWinController, and it is needed here for the
  // same reason: these are REFS, not Redux, so none of the slice-level restore
  // reaches them. `isAnimatingRef` is latched true by the presentation that ran
  // BEFORE the player opened the history, and nothing lowers it — leaving a
  // replay is not a spin. The main effect below then returns at its top gate on
  // every pass, and the restored round shows no win at all.
  //
  // It has to be in BOTH controllers, or the fix works in the base game and
  // silently does nothing for a replay watched from inside free spins.
  //
  // Falling edge ONLY. An entry-side version was tried on the other game and
  // reverted: cancelling the live presentation on the way IN kills the line
  // cycle mid-flight, and that loop is what tidies up after itself, so the
  // board is left dimmed with the boxes and text already gone.
  const replaySessionActive = useSelector(selectReplaySessionActive);
  const prevReplaySessionForWinRef = useRef(replaySessionActive);
  useEffect(() => {
    const prev = prevReplaySessionForWinRef.current;
    prevReplaySessionForWinRef.current = replaySessionActive;

    if (!prev || replaySessionActive) return;

    if (isAnimatingRef.current) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }
    animationCompletedRef.current = false;
    isFirstAnimationRef.current = true;
    // Cleared so the restored round CAN present — leave it and the dedupe still
    // has the round marked as shown, which blocks it instead.
    animatedRoundsRef.current.clear();
  }, [replaySessionActive, clearAllTimeouts]);





  // ── Level charging (scatter -> the box it fills) ─────────────────────────
  //
  // The server has ALREADY moved the ladder by the time the response lands —
  // it arrives when the spin is requested, long before the reels stop. So
  // applyFsState parks the new values on a live free spin and this sequence
  // releases them, which is the whole reason the bulb is allowed to light at
  // the moment the trail hits the box rather than the moment the API answers.
  //
  // Order of one charge, per landed scatter, all of them together:
  //
  //   distribute on the symbol -> trail to the filling box -> +1 bulb
  //
  // then the parked value is committed, which reconciles the walk with the
  // server's exact number — including the wrap to 0 when a level completes.
  //
  // Same state-not-ref rule as the mystery: the main effect waits on this id.
  const [levelChargedRoundId, setLevelChargedRoundId] = useState(UNPRESENTED);
  // Cleopatra's Fire retrigger: raised when the retrigger spin's win has
  // played through ONCE, ending on the scatters (startWinningLinesAnimation) —
  // the popup opens on it. Lowered on every spin (the re-arm effect).
  const [retriggerWinShown, setRetriggerWinShown] = useState(false);
  // Declared HERE, above the charge, because the charge now waits on it and a
  // dependency array is evaluated at render — a declaration further down is a
  // ReferenceError, not a hoisted read. The mystery's own effect is still
  // below; only the state moved.
  const [mysteryRevealedRoundId, setMysteryRevealedRoundId] = useState(UNPRESENTED);
  const levelChargingRef = useRef(false);
  const pendingTier = useSelector(selectFreeSpinsPendingTier);
  const tier = useSelector(selectFreeSpinsTier);
  const tierRef = useRef(tier);
  tierRef.current = tier;

  // Read inside the async sequence, which outlives the render that started it.
  const pendingServerLevel = useSelector(selectFreeSpinsPendingServerLevel);
  const pendingServerLevelRef = useRef(pendingServerLevel);
  pendingServerLevelRef.current = pendingServerLevel;
  const pendingLevelUpSpins = useSelector(selectFreeSpinsPendingLevelUpSpins);
  const pendingLevelUpSpinsRef = useRef(pendingLevelUpSpins);
  pendingLevelUpSpinsRef.current = pendingLevelUpSpins;
  // The same spins split per level crossed — one card per box needs one number
  // each. See announceSpinsPopup.
  const pendingLevelUpSpinsList = useSelector(
    selectFreeSpinsPendingLevelUpSpinsList,
  );
  const pendingLevelUpSpinsListRef = useRef(pendingLevelUpSpinsList);
  pendingLevelUpSpinsListRef.current = pendingLevelUpSpinsList;

  // "Has this round finished being SHOWN" — the same two flags the round's own
  // loop uses to decide the presentation is over, so the charge and the next
  // spin agree about when that is. Subscribed rather than read from the store
  // because the charge effect has to re-run the moment they change.
  const winAnimationsPlayingForCharge = useSelector(selectWinAnimationsPlaying);
  const counterFinishedForCharge = useSelector(selectCounterAnimationFinished);
  const criticalAnimationPlayingForCharge = useSelector(
    selectCriticalAnimationPlaying,
  );



  useEffect(() => {
    if (!spinComplete || isSpinning) return;
    if (levelChargingRef.current) return;

    const roundId = currentRound?.id ?? null;
    if (levelChargedRoundId === roundId) return;

    // ── THE CAP ENDS THE ROUND. Nothing after the spin is owed. ──
    //
    // Vladimir, 2026-09-22, watching a capped round fill its boxes, award more
    // free spins and raise the ADDITIONAL FREE SPINS popup before MAX WIN
    // finally arrived: "as soon as we get that win limit reached, we dont play
    // popups or add free spins or stuff like that. WE END THE GAME AND CLOSE
    // IT." And on the shape of it: "we finish the ongoing spin, and if we
    // capped on that level already, we prevent every further action."
    //
    // The server already agrees — it clamps balance.game to the cap, forces
    // freespins_left to 0 and returns flow.state "closed" in the SAME response
    // (api-contract-mad-labs-freespins.md §10). So the spins this walk would
    // announce do not exist: the round is over and they will never be played.
    // Announcing them promises something the server has already taken away.
    //
    // Further SPINS were already refused — handleFreeSpin's "win cap reached"
    // gate and the loop's isFreeSpinsRoundFinished. This is the one thing left
    // that still ran: the bolts, the boxes, the level-up and its popup.
    //
    // Cut HERE and not earlier, because the WIN itself still plays in full —
    // the Golden Mystery, the coins, the collector, the counter. That is the
    // money he just won and the cap is what it ran into.
    //
    // Marked charged, not skipped silently: the round's ending gates on this
    // id, so returning without it would hold the round open and MAX WIN would
    // never arrive.
    if (freeSpinsCapReached) {
      fsDiag("levelCharge CUT — win limit reached, the round ends here", {
        roundId,
        pendingTier,
      });
      setLevelChargedRoundId(roundId);
      return;
    }

    // ── The WIN goes first, then the scatter, then the level ──
    //
    // This used to run the moment the reels landed, so a spin that both paid
    // and landed a scatter fired the trail over the top of a win the player
    // had not been shown yet. The reference game reads the other way round,
    // and it is the right way round: what this round PAID is the result, the
    // charge is what the result leads to, and a level-up is what the charge
    // leads to. Cause before consequence.
    //
    // Two things have to be finished before the charge may play:
    //
    //   the mystery   it decides what is ON the board, so it precedes
    //                 everything — including the win it creates
    //   the win       the same test the round's own loop uses to decide a
    //                 presentation is over: no overlay up, and a paying round
    //                 has counted out. A 0-win round passes instantly, which
    //                 is correct — there is nothing to wait for.
    if (
      mysteryTargetsByReel(currentRound).length &&
      mysteryRevealedRoundId !== roundId
    ) {
      return;
    }
    if (winAnimationsPlayingForCharge) return;
    // if ((currentRound?.win ?? 0) > 0 && !counterFinishedForCharge) return;
    // Not for a retrigger: its popup opens on the intro's timing, partway
    // into the scatter line, before the counter is released at the pass's end.
    if (
      (currentRound?.win ?? 0) > 0 &&
      !counterFinishedForCharge &&
      !(currentRound?.freeSpinsRetrigger > 0)
    ) {
      return;
    }

    // ── Cleopatra's Fire: the retrigger, and nothing else ──
    //
    // No ladder, no boxes, no gamble (Vladimir, 2026-09-29: "detect respins
    // show them and move on"). The scatters have played their win above; now
    // the Additional Free Spins popup says "+N FREE SPINS", the button takes
    // the spins on its idle beat, it closes itself and the round goes on.
    // `freeSpinsRetrigger` is this step's award, stamped on the round by RGS.
    const retriggerSpins = currentRound?.freeSpinsRetrigger || 0;
    if (retriggerSpins > 0) {
      // The win first, played through to its last clip — the same "play the
      // lines once and stop" the trigger spin uses. The effect re-runs when it
      // lands.
      if (!retriggerWinShown) return;
      levelChargingRef.current = true;
      dispatch(setCriticalAnimationPlaying(true));
      fsDiag("retrigger -> popup", { roundId, spins: retriggerSpins });
      (async () => {
        try {
          // No pause of its own: the effect only gets here once the scatter
          // win has played out (retriggerWinShown above).
          // await new Promise((resolve) => {
          //   new TickerTimeout(resolve, RETRIGGER_POPUP_DELAY_MS);
          // });
          const popup = additionalFreeSpinsControllerRef?.current ?? null;
          if (popup) {
            // The count-only card: nothing on it animates a box.
            popup.setNoBoxClosed(true);
            popup.setSpinCount(retriggerSpins);
            await new Promise((resolve) => {
              dispatch(setShowAdditionalFreeSpins(true));
              popup.startSequence({
                onIdle: () => dispatch(announceFreeSpins()),
                onClosed: resolve,
              });
            });
          }
        } catch (error) {
          console.error("[FreeSpins] retrigger popup failed — releasing:", error);
        } finally {
          // Whatever happened, the button must not stay short of the spins.
          dispatch(announceFreeSpins());
          levelChargingRef.current = false;
          dispatch(setCriticalAnimationPlaying(false));
          setLevelChargedRoundId(roundId);
          fsDiag("retrigger -> done");
        }
      })();
      return;
    }

    // ── The Golden Mystery's own +FS scatters ──
    //
    // They drop INSIDE the feature, so they are never on the screen the ladder
    // is read from — `scatterCellsOnBoard` cannot see them, and the spins they
    // award simply appeared on the counter with nothing said. The server sends
    // the new total (awarded 9 -> 13 on round 1434062); this is the client
    // finally announcing it.
    //
    // One number per feature, summed from the drops. See goldenPlusCells.
    const plusCells = goldenPlusCells(currentRound);
    const featureSpins = plusCells.reduce((t, c) => t + (c.fsCount || 0), 0);

    // Nothing owed — the common spin. Marked done so the main effect's gate
    // opens immediately rather than waiting on a sequence that never runs.
    if (pendingTier === null && !featureSpins) {
      setLevelChargedRoundId(roundId);
      return;
    }

    // ── What fires the bolts ──
    //
    // A scatter on the SCREEN when there is one. Otherwise the feature's own
    // `PLUS` cells, which is the only other way this ladder can move: GDD §3.3
    // gives them "counts toward Level progress", and the SC row states the
    // bonus-game rule as one point each.
    //
    // Only when the ladder ACTUALLY MOVED, though. At the top level nothing
    // advances — round 1434062 sat at `fsLevel=5 collected=16` before and after
    // a feature that awarded four spins — so `pendingTier` is null, there is no
    // pip to fill, and the player simply got spins. Firing bolts at a full
    // ladder would promise progress that cannot happen.
    // ── BOTH lists, and only when the ladder actually moved ──
    //
    // A round can carry a scatter on the screen AND scatters inside the feature.
    // Round 1434212 did, and taking the board's list when it had any meant both
    // `PLUS` cells fired nothing: every point the server counted has to be a
    // bolt the player saw.
    //
    // `pendingTier === null` gates the WHOLE walk, not just the feature half. At
    // the top level nothing advances — round 1434062 sat at `collected=16` before
    // and after a feature that awarded four spins — and a bolt fired then lights
    // a pip the server never gave: `commitFreeSpinsProgress` has nothing pending
    // to correct it with, so the lie stands, and a fourth one would close a box.
    // Spins are still announced; the ladder simply does not move.
    const cells = boxChargeCells(currentRound, pendingTier);

    if (!cells.length && !featureSpins) {
      // The ladder moved with no scatter on screen to explain it. Commit
      // rather than strand the value — a box that never fills is worse than
      // one that fills without a flourish.
      fsDiag("levelCharge: pending with no scatter on board — committing");
      dispatch(commitFreeSpinsProgress());
      setLevelChargedRoundId(roundId);
      return;
    }

    levelChargingRef.current = true;
    dispatch(setCriticalAnimationPlaying(true));
    fsDiag("levelCharge -> start", { cells: cells.length, pendingTier });

    (async () => {
      // ── The LOCK is released in a FINALLY, and that is not dressing ──
      //
      // This block raises criticalAnimationPlaying and holds it through the
      // whole walk: the bolts to the pips, the box filling, the popup, the
      // room re-dressing and the conversion. Everything in it is async and
      // touches spines, a popup controller and the scene. If any of it throws,
      // the release below never runs and the round is stranded — no spin
      // button, for the rest of the session, with nothing on screen to say
      // why. The scatters already had their own guard; the popup and the room
      // commit after them had none.
      //
      // The Golden Mystery effect states the same rule in the same words and
      // for the same reason. Losing the flourish costs the round's
      // presentation; losing the lock costs the session.
      try {
        const reelset = reelsetControllerRef.current;
        const animator = levelChargingAnimatorRef?.current ?? null;

        // Captured BEFORE the walk. Banking a box commits the rung, which clears
        // the parked values — and the popup afterwards still needs to know what
        // was won.
        const levelUpTo = pendingServerLevelRef.current;
        // Where the ladder stood before this walk — the base each completed box
        // steps up from. See walkedLevel below.
        const levelAtWalkStart = store.getState().freeSpins.serverLevel ?? 1;
        const levelUpSpins = pendingLevelUpSpinsRef.current;
        // What the Nth box banked this walk is worth. The server sends one
        // entry per level crossed; an older payload, or a shape we have not
        // seen, falls back to giving the whole amount to the first box — which
        // is what the single total always meant.
        const levelUpSpinsByBox = pendingLevelUpSpinsListRef.current || [];
        const levelUpSpinsForBox = (index) => {
          if (levelUpSpinsByBox.length) return levelUpSpinsByBox[index] || 0;
          return index === 0 ? levelUpSpins || 0 : 0;
        };

        // ── One bulb at a time, in order ──
        //
        // The trails are staggered and overlap on purpose, so two can land while
        // a box is mid-conversion. Arrivals are therefore queued through a
        // single chain: each waits for the one before it to finish, which is
        // what lets a box bank and hand the next bulb to the box above it
        // instead of every scatter piling into the one that was filling when
        // the spin started.
        let bulbs = Promise.resolve();
        let walkedLevel = null;

        // ── The walk counts for ITSELF, and never re-reads the ref ──
        //
        // `tierRef.current = tier` is assigned during RENDER, so it only moves
        // when React re-renders. The bulbs are queued as `bulbs.then(...)` —
        // adjacent MICROTASKS — and a dispatch does not re-render between two
        // of them. So the second bulb of a spin read the tier from before the
        // first one landed.
        //
        // Two scatters over a box with three pips is the case that shows it:
        // the first fills the box and resets the tier to 0, the second reads a
        // stale 3, and the two disagree about which pip is being filled. The
        // box is left in a state the next repaint does not expect, and the
        // FOLLOWING round's server value re-syncs it — which is why a pip goes
        // dark and then two light at once a spin later.
        //
        // Seeded once from the ref, which is correct at this instant because
        // nothing in this walk has dispatched yet, and advanced locally from
        // there. Redux is still dispatched for every step — the boxes read it —
        // but this arithmetic no longer waits on a render to see its own work.
        let walkTier = tierRef.current;

        const onBulbArrived = () => {
          bulbs = bulbs.then(async () => {
            const next = walkTier + 1;

            if (next < FREE_SPINS_TIERS_PER_LEVEL) {
              walkTier = next;
              dispatch(setFreeSpinsTier(next));
              return;
            }

            // The box is full.
            // ── One rung per box, in order ──
            //
            // This was `walkedLevel ?? levelUpTo`, which pinned the SAME value
            // for the whole walk — so two boxes completing in one spin both
            // committed the final level and the first box was orphaned at the
            // rung it never got.
            //
            // A completed box is worth exactly one rung, so each one steps up
            // from the last, capped at the level the server actually reported.
            walkedLevel =
              levelUpTo === null || levelUpTo === undefined
                ? null
                : Math.min((walkedLevel ?? levelAtWalkStart) + 1, levelUpTo);

            // Lights the fourth bulb AND holds the box — see
            // holdFilledBoxForConversion for why that one bulb cannot go
            // through Redux like the others.
            //
            // HELD, not played. The flourish belongs to the moment the dialog
            // closes — playing it here would close the box behind the popup,
            // where nobody sees it.
            //
            // The hold is what makes the rung commit below safe: a repaint would
            // otherwise lock() this box, and lock() is a HOLD on `inactive`. A
            // held box is skipped entirely and stays full and lit.
            freeSpinsGameViewControllerRef?.current?.holdFilledBoxForConversion?.();
            if (walkedLevel !== null) {
              dispatch(commitFreeSpinsServerLevel(walkedLevel));
            }
            // Locally first, so the NEXT bulb in this chain starts from an
            // empty box whether or not React has rendered — see walkTier.
            walkTier = 0;
            dispatch(setFreeSpinsTier(0));
          });
        };

        // ── All at once ──
        //
        // Every scatter is given ITS OWN bulb before anything is fired, so there
        // is nothing for one trail to learn from another and no reason to space
        // them out. The staggered version existed only because the target was
        // resolved at launch and had to wait for the box in front of it; with
        // the bulbs assigned up front that constraint is gone, and the round
        // reads faster.
        //
        // The Nth scatter this spin fills the Nth bulb after the current tier,
        // wrapping onto the next box when it passes the fourth.
        const startTier = tierRef.current;
        const scene = freeSpinsGameViewControllerRef?.current ?? null;

        // ── Announcing, one rung at a time ──
        //
        // Called at every box that banks, and once more at the end for spins
        // no box paid for. Everything the popup used to do at the end of the
        // walk happens here instead; the only new part is that it can run more
        // than once.
        //
        // `roomLevel` is the rung THIS card is about — null when no box closed,
        // which is what tells it to play the glow clip and leave the room alone.
        let announced = 0;
        const announceSpinsPopup = async ({ spins, roomLevel }) => {
          const noBoxClosed = roomLevel === null;
          const popup = additionalFreeSpinsControllerRef?.current ?? null;
          // Artwork only — the rung just won, or the one being played when no
          // box completed. The popup falls back for a multiplier it has no
          // clips for.
          const cardLevel =
            roomLevel ?? store.getState().freeSpins.serverLevel;

          fsDiag("spins -> popup", {
            level: roomLevel ?? "none",
            noBoxClosed,
            multiplier: multiplierForServerLevel(cardLevel),
            spins,
          });

          if (spins > 0 && popup) {
            announced += spins;
            popup.setNoBoxClosed(noBoxClosed);
            popup.setLevel(multiplierForServerLevel(cardLevel));
            popup.setSpinCount(spins);

            await new Promise((resolve) => {
              dispatch(setShowAdditionalFreeSpins(true));
              popup.startSequence({
                // ── The one beat the round's news is delivered ──
                //
                // The card is on screen saying "+N FREE SPINS". Everything the
                // player is being TOLD moves now, and nothing moves before:
                //
                //   the room     only a real level-up changes it
                //   the counter  always, because the popup always announces
                //                spins — the level's, the feature's, or both
                onIdle: () => {
                  if (roomLevel !== null) {
                    dispatch(commitFreeSpinsRoomLevel(roomLevel));
                  }
                  dispatch(announceFreeSpins());
                },
                onClosed: resolve,
              });
            });

            // The popup lowers criticalAnimationPlaying in its own
            // finishAnimation and has no idea a sequence still owns the screen.
            dispatch(setCriticalAnimationPlaying(true));
          } else if (roomLevel !== null) {
            // A rung with nothing to announce — no popup, or no spins attached.
            // The room still has to move and the box still has to shut.
            fsDiag("levelUp with no popup — committing the room");
            dispatch(commitFreeSpinsRoomLevel(roomLevel));
          }

          // ── The box closes itself, whatever route got here ──
          //
          // holdFilledBoxForConversion runs whenever a fourth bulb lands, so a
          // box can be held with no level to announce — the ceiling rung does
          // exactly that. A hold that is never released is skipped by every
          // repaint for the rest of the round. Not awaited: the round is let go
          // in the same breath rather than standing and watching a box shut.
          freeSpinsGameViewControllerRef?.current?.playHeldConversion?.();
          fsDiag("spins -> done");
        };

        // ── The volley STOPS at a box ──
        //
        // Three pips lit and two scatters landing: the first shuts the box and
        // the second belongs to the box above it. The player has to be told
        // about the level BEFORE that second one flies, or the popup arrives
        // over a board that has already moved on.
        //
        //   scatter A -> 4th pip -> box shuts -> popup -> convert -> scatter B
        //
        // So the cells are cut into runs that END on a box-filling bulb, and
        // each run is fired the way the whole volley used to be: all at once,
        // arrivals queued. Only the runs are sequential.
        //
        // The bulb number is assigned HERE, once, and carried — it used to be
        // derived from the map index, which no longer counts across runs.
        // ── Targets are resolved HERE, before a single box banks ──
        //
        // `bulbPosition` counts from the box currently being FILLED, and
        // banking a box moves that. While the whole volley was fired at once
        // nothing could move underneath it; now a run waits for a popup, and
        // the run after it would resolve its offset against a base that has
        // already advanced — every later bulb one box too far.
        //
        // Resolved up front, against the base the offsets were computed from,
        // which is exactly what the all-at-once version did by accident of
        // timing. The boxes do not move on screen, only which one is filling.
        const runs = [];
        let run = [];
        cells.forEach((cell, index) => {
          const bulbNumber = startTier + index;
          const fillsBox =
            (bulbNumber + 1) % FREE_SPINS_TIERS_PER_LEVEL === 0;
          const boxOffset = Math.floor(bulbNumber / FREE_SPINS_TIERS_PER_LEVEL);
          const tearIndex = (bulbNumber % FREE_SPINS_TIERS_PER_LEVEL) + 1;
          run.push({
            ...cell,
            bulbNumber,
            fillsBox,
            boxOffset,
            tearIndex,
            target: scene?.bulbPosition?.(boxOffset, tearIndex) ?? null,
          });
          if (fillsBox) {
            runs.push(run);
            run = [];
          }
        });
        if (run.length) runs.push(run);

        // How many boxes this walk has banked so far — the popup announces one
        // rung at a time, so each one needs to know which it is.
        let boxesFilled = 0;

        for (const cellsInRun of runs) {
          await Promise.all(
            cellsInRun.map(async ({
              reelIndex,
              row,
              symbolId,
              fsCount,
              golden,
              boxOffset,
              tearIndex,
              target,
            }) => {
              const overlay = reelset?.overlayReelControllers?.[reelIndex];

              try {
                // Read the cell's centre BEFORE anything plays. The trail leaves
                // from where the symbol is standing, not from wherever its clip
                // has pushed it to.
                //
                // A Golden cell answers for itself. It is exempt from the ordinary
                // presentation — eight guards keep the pooled spines off it — so
                // both the point and the clip come from its own controller. The
                // point is the same one either way: the overlay cell's container,
                // which the layout positions and nothing animates.
                const goldenCell = golden
                  ? overlay?.goldenCell?.(row) ?? null
                  : null;

                const startPos = goldenCell
                  ? goldenCell.gridCenter?.(animator?.trailContainer)
                  : overlay?.symbolCenterIn?.(row, animator?.trailContainer);

                // The flourish and the trail start on the SAME frame — the trail
                // is what `distribute` is distributing. Both scatters ship the clip:
                // `bs` for a +N, `sc` for the plain one that charges and pays no
                // spins. The skin is the +N it is paying out, and null for `sc`.
                // ── ONE picture per cell: the spine, not the spine AND the flat ──
                //
                // The reel keeps a static texture under every landed symbol, and
                // an overlay clip is drawn OVER it — so playing `distribute`
                // without hiding the flat one leaves two scatters stacked, the
                // animated one moving and the painted one sitting still behind
                // it. That is the doubling.
                //
                // The house rule, and the same two lines the win cycle already
                // uses before syncWinAnimation: hide the texture, then play.
                // Restoring it is NOT done here on purpose — the spin's own
                // cleanup calls showAllSymbolTextures with the overlay already
                // down, so the flat picture comes back in the same turn the spine
                // goes away and no frame ever shows both. Restoring it here would
                // hand the cell between two layers across a RAF, which is the one
                // thing that must not happen (see the mystery reveal's handover).
                //
                // A Golden cell is exempt: it owns its own picture end to end and
                // `hideStrip: true` below is how it hides its own.
                if (!goldenCell) {
                  reelset?.reelControllers?.[reelIndex]?.hideSymbolTexture?.(row);
                }

                const distributing = goldenCell
                  ? goldenCell.playCellSpine?.({
                      spineName: symbolData[symbolId]?.spine || "bs",
                      clip: "distribute",
                      skin: plusSkinForCount(fsCount),
                      hideStrip: true,
                      // ── INSIDE the window, like every other cell picture ──
                      //
                      // playCellSpine has two homes. The default is the effects
                      // layer, which is right for a multiplier's burst or a
                      // magnet's strikes — things drawn to SPILL past the cell.
                      // It is wrong here: this clip is the scatter ITSELF, not an
                      // effect over it, and on that layer it draws at full cell
                      // size over the frame and hangs outside the golden window.
                      // The symbol appeared to climb out of the mystery to fire
                      // its bolt.
                      //
                      // A symbol that landed inside the mystery never leaves it.
                      //
                      // `inWindow` rather than `inCell`: both keep the symbol in
                      // the golden window, but inCell is the MASKED container and
                      // `distribute` grows an eclipse well past the cell — masked,
                      // the flourish is cut at the edge and the bolt looks like it
                      // leaves from nothing. This layer is inside the frame's own
                      // window and unmasked, so the scatter stays where its
                      // picture was AND its burst still reads.
                      inWindow: true,
                    })
                  : overlay?.playSymbolClipOnce?.(row, symbolId, "distribute");


                const travelling = animator?.fireTrail?.(startPos, target, {
                  onArrival: onBulbArrived,
                });

                await Promise.all([distributing, travelling]);
              } catch (error) {
                // One scatter failing must not strand the rest, or the round.
                fsDiag("levelCharge: a scatter failed", { reelIndex, row });
              }
            }),
          );

          // This run's bulbs have all landed and, if it ended on one, the box
          // has banked itself inside onBulbArrived.
          await bulbs;

          if (cellsInRun[cellsInRun.length - 1]?.fillsBox) {
            // ── The feature's spins ride with the FIRST card ──
            //
            // A round can add spins two ways at once: a box completing, and the
            // Golden Mystery's own `PLUS` cells. Announcing those separately
            // would put two cards on screen back to back, which reads as the
            // game saying the same thing twice. They are folded into the first
            // card instead, so the number of popups is exactly the number of
            // boxes that banked — never one more.
            const levelSpins = levelUpSpinsForBox(boxesFilled);
            await announceSpinsPopup({
              spins: levelSpins + (boxesFilled === 0 ? featureSpins : 0),
              // The rung THIS box just won. `walkedLevel` is stepped one per
              // box inside the walk, so it is that box's own level and not the
              // round's final one — the room re-dresses a rung at a time.
              roomLevel: walkedLevel,
            });
            boxesFilled += 1;
          }
        }

        // Every bulb has landed and every box that filled has been announced.
        // Only the tier is settled here — the rung moved inside the walk.
        await bulbs;
        dispatch(commitFreeSpinsProgress());

        // ── Anything no box paid for ──
        //
        // The common case is a feature-only round: the Golden Mystery's `PLUS`
        // cells add spins and the ladder never moves, so nothing banked and
        // this is the only card shown. `roomLevel: null` makes it the glow
        // clip, which is the whole point — no box closes here.
        //
        // The second case is bookkeeping: if the server's per-level list ever
        // pays more than the boxes this walk actually filled, the remainder is
        // still owed to the player.
        //
        // A level reported with no box banked keeps the old behaviour — the
        // room moves and the card shows the rung closing, popup or not.
        // ── The room must end on the rung the SERVER reported ──
        //
        // Each banked box moves it one, which is what makes a two-box spin
        // re-dress a rung at a time. But the server can report a jump the
        // boxes do not account for — a level reached with fewer boxes banked
        // in this walk than rungs climbed — and `walkedLevel` is capped at one
        // per box, so the room would stop short and stay there.
        //
        // Committed here, after every popup, and only when it actually lags.
        // Idempotent: the reducer takes the level it is given.
        if (
          levelUpTo !== null &&
          levelUpTo !== undefined &&
          boxesFilled > 0 &&
          walkedLevel !== levelUpTo
        ) {
          dispatch(commitFreeSpinsRoomLevel(levelUpTo));
        }

        const leftToAnnounce =
          featureSpins + (levelUpSpins || 0) - announced;
        if (leftToAnnounce > 0 || (boxesFilled === 0 && levelUpTo !== null)) {
          await announceSpinsPopup({
            spins: Math.max(0, leftToAnnounce),
            roomLevel: boxesFilled === 0 ? levelUpTo : null,
          });
        }

        // ── Nothing may leave this walk still holding the counter ──
        //
        // The popup's onIdle is the beat, and by here it has already run — so
        // for that path this is a no-op. It matters for every OTHER way out:
        // no popup controller, a level with no spins, a box that filled with
        // nothing to announce. A held count that is never released reads low
        // on the button for the rest of the round, which is a worse fault than
        // the early climb the hold exists to prevent.
        dispatch(announceFreeSpins());

      } catch (error) {
        // Named loudly: a silent catch here turns a broken level-up into a
        // room that simply never advances, which is the hardest kind of fault
        // to find from the outside.
        console.error("[FreeSpins] level charge failed — releasing:", error);
      } finally {
        levelChargingRef.current = false;
        dispatch(setCriticalAnimationPlaying(false));
        // Published even on a failure: the spin still has wins to pay, and a
        // charge that did not finish must not stop them being presented.
        setLevelChargedRoundId(roundId);
        fsDiag("levelCharge -> done");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    isSpinning,
    currentRound,
    pendingTier,
    levelChargedRoundId,
    // What the charge now WAITS on. Without these the effect never re-runs when
    // the mystery closes or the counter finishes, and a charge that was
    // correctly held back would simply never play.
    //
    // The mystery is watched through criticalAnimationPlaying, which it clears
    // on its last frame, rather than through mysteryRevealedRoundId — that
    // state is declared further down and a dependency array is evaluated during
    // render, so naming it here would be a ReferenceError. Nothing about the
    // mystery moved to make this work.
    criticalAnimationPlayingForCharge,
    winAnimationsPlayingForCharge,
    counterFinishedForCharge,
    // The cut above.
    freeSpinsCapReached,
    // The retrigger popup waits on its win.
    retriggerWinShown,
  ]);

  // ── Mystery (B1) reveal ─────────────────────────────────────────────────
  //
  // The base game's block, verbatim — same feature, same order, different
  // round source (freeSpins.currentRound) and different spin flags.
  //
  // Runs BEFORE anything is evaluated, because that is the game's rule: "B1
  // resolves before win evaluation — the board that pays is not the board that
  // stopped" (invariant I1). It also has to run on LOSING rounds, which is why
  // it cannot live inside the win presentation: that returns early at the
  // triggerWin guard.
  //
  // The round id is held in STATE rather than a ref because the main effect
  // waits on it. A ref is invisible to React and the presentation would never
  // re-run — the same trap that has bitten this codebase six times.
  const mysteryRunningRef = useRef(false);

  // ── How much ceremony this board earns ──
  //
  // Scored from POTENTIAL — what the board could pay if the mysteries became
  // the best symbol for it — not from what it actually paid. A board that
  // threatens a huge win deserves the long build-up whether or not it lands;
  // that tension IS the feature. See Games/Shared/mysteryDrama.
  //
  // Kept in a ref because the reveal reads it from inside an async sequence
  // that outlives the render which computed it.
  const dramaPaytable = useSelector(selectPaytable);
  const dramaLines = useSelector(selectFreeSpinsWinningLines);
  const dramaBet = useSelector(selectBet);
  const dramaBaseBet = useSelector(selectBaseBet);
  const mysteryClipRef = useRef(MYSTERY_DRAMA_CLIPS.small);

  // ── Waiting for a room that does not exist yet ──
  //
  // The base game's copy of this effect had a race that only showed on a
  // reload, and the room can only be worse: `spinComplete` defaults to true and
  // the round is restored from the record, so both are true before a single
  // reel of the room has been built. The reveal then ran against nothing —
  // `overlay?.playRevealSequence?.()` is optional chained, so every missing
  // controller was a SILENT skip — and the round was published as revealed, so
  // it could never run again. The cells came back as plain symbols and the win
  // that followed animated the mystery spine, because their identity had never
  // been rewritten either.
  //
  // The tick is what re-runs it. A ref cannot: React does not see a ref change,
  // which is the trap this codebase has hit repeatedly.
  const [mysterySceneTick, setMysterySceneTick] = useState(0);
  const mysterySceneWaitsRef = useRef(0);

  useEffect(() => {
    if (bonusBoardActive) return;
    if (!spinComplete || isSpinning) return;
    if (mysteryRunningRef.current) return;

    // ── The mystery goes FIRST, ahead of everything ──
    //
    // It decides what is on the board, so the win it creates cannot be judged
    // before it has opened, and the scatter charge that follows the win cannot
    // run before either. The charge used to be ahead of this and is now behind
    // the win — see the charge effect for why that order is the right one.
    //
    // Both still hold the same UI lock, and the chain mystery -> win -> charge
    // is what keeps them from fighting over it.
    const roundId = currentRound?.id ?? null;
    if (mysteryRevealedRoundId === roundId) return;

    // ── THE ARRIVAL BOARD IS A PHOTOGRAPH, NOT A FEATURE ──
    //
    // The room opens on a copy of the trigger spin's screen, and that copy
    // carries the base game's `storage` — deliberately, because the board
    // cannot be drawn without it: `mystery_feature` says which cells were
    // mysteries and `coins_feature` is what each Golden cell finally held.
    //
    // Read as a round to present, though, those two fields are a whole Golden
    // Mystery waiting to be played. So the room ran it again: twenty-four lids,
    // the build-up, the doors, the coins, three magnets and the multiplier —
    // a second performance of a feature that had just finished downstairs, on a
    // board whose money was already in the balance. Vladimir, 2026-09-22: "when
    // we enter free spins the entire golden mystery sequence repeats itself...
    // we just need the textures we had when we entered."
    //
    // The textures are already there. FreeSpinBasicReel paints every Golden
    // cell as a flat composite on this board, and the overlay marks those rows
    // static so nothing animates them. All that was missing is this refusal.
    //
    // ONE signal, and it is `played === 0`: no free spin has resolved, so
    // anything on this board belongs to the base game.
    //
    // NOT `isTriggerBoard`. That flag is stamped by arrivalBoardFrom, and the
    // slice builds the COMPLETION board with the same helper — so the last
    // free spin of a finished round came back wearing it. Testing it here
    // refused that round's replay: the reveal, the golden spin and the win all
    // stopped happening on an ending reload. Vladimir, 2026-09-23: *"WHEN DID
    // I TELL YOU TO NOT REPLAY A ROUND ON RELOAD?"* He did not. An ending
    // reload replays in full, like any other round.
    //
    // ── And it must PUBLISH before returning ──
    //
    // Not for the win presentation — that refuses this board on its own, one
    // guard above its mystery test. It is the LEVEL CHARGE: it holds for
    // `mysteryRevealedRoundId !== roundId` whenever the board has mystery
    // cells, and this board has twenty-four of them. Returning silently would
    // park the entry sequence for ever — the card up, its pips never flying,
    // the level box never filling.
    //
    // Safe to publish a null id: the re-arm effect resets this on the rising
    // edge of `isSpinning`, so the first real free spin reveals normally.
    if (freeSpinsPlayed === 0) {
      fsDiag("mystery reveal BLOCKED by arrival board (base game played it)", {
        played: freeSpinsPlayed,
        cells: mysteryTargetsByReel(currentRound).length,
      });
      setMysteryRevealedRoundId(roundId);
      return;
    }

    const targets = mysteryTargetsByReel(currentRound);
    if (!targets.length) return;

    // ── Not while a popup owns the screen ──
    //
    // The same two the win presentation waits for, and for the same reason: a
    // reveal that plays under the intro or the mid-round reload popup is a
    // reveal the player never sees. Both are in the deps, so their close
    // re-runs this effect and the restored round reveals THEN.
    //
    // ── ...and the free-rounds message ──
    //
    // A reload during a campaign opens on it (the INFO popup), and this room
    // did not wait: on a completion reload the last round's mystery revealed
    // underneath it. The base game waits on the same flag for the same reason.
    // Vladimir, 2026-09-24: *"we have to block it the same way we do for
    // reload dialog."*
    //
    // ── ...and not under the intro's wipe ──
    //
    // The base game's reveal waits for the same flag. Started at the black
    // cover, a completion reload's reveal ran BEFORE the scene's outro opener
    // and had its lock cleared by that opener's boot unlock, so the opener
    // never saw it and sat out its whole 5s bound. Vladimir, 2026-09-25: "i
    // want same waiting time i dont care if i reload or not."
    if (
      introPopupActive ||
      introTransitionActive ||
      freeSpinsReloadActive ||
      infoPopupActive
    ) {
      return;
    }

    // ── Not before the cells it reveals exist ──
    //
    // Every reel this round touches must have BOTH controllers: the overlay
    // runs the sequence, and the reel underneath takes the identity and the
    // settled texture at the end. Missing either one silently half-finishes the
    // cell, which is exactly what a reload used to do to all of them.
    const scene = reelsetControllerRef.current;
    const sceneReady =
      !!scene &&
      targets.every(
        ({ reelIndex }) =>
          !!scene.overlayReelControllers?.[reelIndex] &&
          !!scene.reelControllers?.[reelIndex],
      );
    if (!sceneReady) {
      // Try again next frame, then every half second — it never gives up. A
      // reveal that stops waiting is a round that never presents; the base
      // game's twin had exactly that fault. The slow poll keeps a room that is
      // late to build from costing a state bump every frame.
      mysterySceneWaitsRef.current += 1;
      if (mysterySceneWaitsRef.current <= MYSTERY_SCENE_MAX_WAITS) {
        requestAnimationFrame(() => setMysterySceneTick((t) => t + 1));
      } else {
        new TickerTimeout(() => setMysterySceneTick((t) => t + 1), 500);
      }
      return;
    }
    mysterySceneWaitsRef.current = 0;

    // The order the Golden tracks stop in — a property of the round, so it is
    // worked out once for the whole board. See goldenStopOrder.
    const stopOrder = goldenStopOrder(currentRound);

    const drama = mysteryDrama(currentRound, {
      paytable: dramaPaytable,
      lines: dramaLines,
      bet: dramaBet,
      baseBet: dramaBaseBet,
    });
    mysteryClipRef.current =
      MYSTERY_DRAMA_CLIPS[drama.tier] ?? MYSTERY_DRAMA_CLIPS.small;

    mysteryRunningRef.current = true;
    // Locks the UI. Held until every cell has settled, so the free-spin loop
    // cannot fire the next spin over a reveal in progress.
    dispatch(setCriticalAnimationPlaying(true));

    (async () => {
      // ── The lock is released in a FINALLY, and that is not defensive dressing
      //
      // This block raises criticalAnimationPlaying and holds it for the whole
      // reveal and the Golden loop behind it. Everything inside is async and
      // touches spines, textures and captures; if any of it throws, the release
      // below never runs and the ROOM is bricked — no spin, no outro, for the
      // rest of the session. The reveal failing should cost the round's
      // animation, never the feature.
      try {
      // Let the landing finish its own texture→spine handover first.
      //
      // useLandingAnimations hides a landed cell's static ONE RAF after it
      // shows the spine, on purpose, so the two overlap. `spinComplete` fires
      // before that RAF has run for the last reel, so taking the cells here
      // starts a reveal over a static that is still at full alpha — the trace
      // shows reel 4 hidden three lines AFTER `reveal START`.
      await new Promise((resolve) => requestAnimationFrame(resolve));

      // ── The lamp lights while the mystery is still deciding ──
      //
      // The base game's block, word for word, on the room's own scene — see
      // useWinController. Same rig, same clips, same beat: fired one line
      // before every cell starts its build-up, so the scene's wait and the
      // cells' wait are the same thing.
      //
      // Fitted to the window rather than looped and cut, because the clip is
      // 800ms and the two anticipations are 500ms and 2000ms — a plain loop
      // leaves the lamp mid-flash at the exact frame the doors open. Fitted, it
      // ends on a frame the animator drew: two quick flashes for a small
      // mystery, four for a big one, which is a signal the player can read.
      //
      // The window is the WAIT PLUS THE REVEAL. The lamp is the scene saying
      // "something is happening", and that is still true while the doors are
      // opening — the symbol is not shown until `my.reveal` ends.
      //
      // Both clips are read off the rig, and the reveal's NAME comes from the
      // sequence the cells actually run (`sequence.open`), so a config change
      // moves the window with it rather than leaving this behind.
      const mysteryReveal = symbolData[MYSTERY_SYMBOL_ID]?.reveal;
      // ── The rig is ASKED FOR, never named here ──
      //
      // This was the literal "my", which silently became the wrong rig the day
      // the mystery moved to `my`: the lamp window would have been measured
      // against clips the cells no longer play. The clip NAMES already come off
      // the config for exactly this reason — the skeleton now does too.
      const mysterySpine = symbolData[MYSTERY_SYMBOL_ID]?.spine;
      const revealClip = mysteryReveal?.sequence?.open?.[0];
      const openMs = revealClip
        ? (SpineAnimationData.getAnimationDuration(mysterySpine, revealClip) ?? 0)
        : 0;
      // ── The reveal is over when the LID has gone, not when the clip ends ──
      //
      // `fadeOutEndMs` is that moment: the doors land open and nothing of the
      // mystery is left. The clip runs on past it drawing nothing, and the
      // sequence does not wait for it, so neither does the lamp. Without a
      // stated end the fade follows the whole clip, and the two add up. See
      // RevealSymbolController's fade.
      const revealMs = mysteryReveal?.fadeOutEndMs
        ? mysteryReveal.fadeOutEndMs
        : openMs + (mysteryReveal?.fadeOutMs ?? 0);
      const anticipationMs =
        (SpineAnimationData.getAnimationDuration(
          mysterySpine,
          mysteryClipRef.current,
        ) ?? 0) + revealMs;
      freeSpinsGameViewControllerRef?.current?.playAccentForWindow(
        SCENE_ACCENTS.MYSTERY_WAIT,
        anticipationMs,
      );

      // ── The scene's answer to a GOLDEN MYSTERY ──
      //
      // The base game's rule, in the room: see the long note on the copy in
      // useWinController. The reel lightning used to fire on the LONG build-up
      // as a second tier of anticipation; it is spent here instead on the one
      // outcome that earns it, because a long build-up reveals an ordinary
      // symbol just as often.
      //
      // Both are HELD from that moment to the end of the feature, and fired
      // from the CELL at the frame the doors part (onRevealed) — anything sent
      // earlier lights the reels while the doors are still shut and gives away
      // what is behind them.
      //
      // Once, not once per cell: every cell opens together and each calls back.
      //
      // The ROOM's controller, not the base game's. Same shared accents module
      // over the same rig — the two scenes are byte-identical exports — but the
      // spine on screen is the bonus one.
      let sceneLitForGolden = false;
      const litSceneForGolden = () => {
        if (sceneLitForGolden) return;
        sceneLitForGolden = true;
        const view = freeSpinsGameViewControllerRef?.current;
        view?.playAccent(SCENE_ACCENTS.MYSTERY_WAIT, { loop: true });
        view?.playAccent(SCENE_ACCENTS.MYSTERY_WAIT_STRONG, {
          loop: true,
          track: SCENE_ACCENT_TRACKS.REELS,
        });
      };

      const reelset = reelsetControllerRef.current;

      // ── The `+FS` reveals, started but NOT waited for ──
      //
      // A cell that lands the golden scatter opens and spins its own strip,
      // and the rest of the board must not stand still while it does. Vladimir:
      // *"they all spin and stop independently, once we land on this it spins
      // for itself, while in the background the golden mystery proceeds."*
      //
      // So each one is launched where its cell lands and its promise is kept
      // here. They are joined once, below, before the board is read for the
      // payout — nothing may be processed while a window is still deciding
      // what is in it.
      const plusReveals = [];
      // The cells whose window actually opened, as `reel,row`. The final paint
      // needs it: an unrevealed `+FS` paints as the CLOSED door by design, so a
      // cell that has just performed its reveal must say so or the board
      // repaints a shut window over an open one.
      const plusRevealed = new Set();

      // ── Which cells have been ASKED to open, marked synchronously ──
      //
      // `plusRevealed` is marked when a reveal RESOLVES, which is too late to
      // stop a second one starting: the landing's reveal is deliberately not
      // awaited, so the loop can reach a later iteration while it is still
      // playing.
      //
      // It only matters if the server ever re-lists a `+FS` cell. In the base
      // game that is routine — a BLANK gamble scatter is re-listed on every
      // following iteration so it can re-roll — and `bs` replaces SC1 in the
      // bonus (invariant I14). A `+FS` always awards, so by the BE doc's rule
      // ("if it awards nothing it re-rolls") it should never come back.
      //
      // Should, not cannot. If one did, this room would run the WHOLE sequence
      // on it again — doors shut, doors open — on a window the player is
      // already looking into, which is the exact fault the base game's re-roll
      // was built to avoid. Vladimir, 2026-09-22: "if there is no blank then we
      // just land on bs and thats it."
      //
      // So a cell opens ONCE. Cheap, and it makes the room's behaviour the
      // documented one rather than whatever the payload happens to be.
      const plusOpened = new Set();

      // All cells at once, and all to the same symbol — invariant I2.
      await Promise.all(
        targets.flatMap(({ reelIndex, rows, replacementId }) => {
          const overlay = reelset?.overlayReelControllers?.[reelIndex];

          return rows.map(async (row) => {
            // ── B2: the cell becomes a respin, not a symbol ──
            //
            // A Mystery that reveals into the bonus symbol is the Golden
            // Mystery (invariant I3 — B2 can never land any other way). Nothing
            // below applies to it: there is no symbol to settle on, no
            // composite to bake and no identity to rewrite, because the board
            // already holds symbol 10 at this cell and the feature decides what
            // it pays.
            //
            // The whole cell — build-up, doors, the golden lid, the track —
            // belongs to its own controller. See playGoldenMysterySequence.
            if (replacementId === BONUS_SYMBOL_ID) {
              const goldenOutcome = goldenOutcomeAt(currentRound, reelIndex, row);
              await overlay?.playGoldenMysterySequence?.(row, {
                outcome: goldenOutcome,
                // Deliberately NOT the round's own outcomes. A track built
                // from them shows the player the answer before it lands — see
                // GOLDEN_STRIP_ITEMS.
                items: null,
                anticipationClip: mysteryClipRef.current,
                stopOrder: stopOrder.get(`${reelIndex},${row}`) ?? 0,
                onRevealed: litSceneForGolden,
              });

              // ── It landed the golden scatter: open it, and move on ──
              //
              // Started and deliberately NOT awaited — this function returning
              // is what lets the other cells carry on and the loop below begin.
              // The promise goes in the list above and is joined before the
              // payout is read.
              if (goldenOutcome?.symbolId === SCATTER_3_SYMBOL_ID) {
                plusOpened.add(`${reelIndex},${row}`);
                const plus = overlay?.playRevealSequence?.(
                  row,
                  SCATTER_3_SYMBOL_ID,
                  goldenOutcome,
                );
                if (plus) {
                  plusReveals.push(
                    // A failed reveal must not take the round with it: the
                    // spins are already awarded by the server and the cell
                    // still paints below.
                    // MARKED ONLY IF IT RAN. playRevealSequence is async, so
                    // the promise itself says nothing — it resolves the same
                    // way when the sequence returned at a guard (no entry, no
                    // pooled spine). It now reports a boolean, and only a true
                    // one means a window really opened and the settled `+N` is
                    // the honest picture for that cell.
                    plus
                      .then((ran) => {
                        if (ran) plusRevealed.add(`${reelIndex},${row}`);
                      })
                      .catch((error) => {
                        fsDiag("plus reveal failed", {
                          reel: reelIndex,
                          row,
                          error: String(error),
                        });
                      }),
                  );
                }
              }

              // The picture the cell keeps is painted AFTER the loop, from the
              // board's final state — see the block below runGoldenMysteryLoop.
              // Painting it here would freeze iteration 1: a cell that started
              // as a x2 would ride the reel out as a x2 even though it ended as
              // a magnet holding a total.
              return;
            }

            // ── The mystery is a LID, not a window ──
            //
            // Identical to the base game's block, deliberately: both read the
            // same `symbolData[9].reveal` config, so the presentation has to
            // match or the two games disagree about what a mystery is.
            //
            // Nothing is injected into it. What the doors open onto is the
            // ordinary symbol standing on the reel underneath, painted there
            // by `onOpening` while the cell is still covered — the engine's
            // hidden-texture swap, the same one the expanding wild uses.
            //
            // replaceSymbolAtPosition and not setSymbolTextureOnly, because
            // the cell really does BECOME that symbol: it celebrates on its
            // own pooled spine like any other, and the next spin reads it off
            // the controller's own array. There is no composite to bake, no
            // frame to keep and no pair to park.
            //
            // How long it teases is decided per round, not per symbol: a
            // mystery that pays nothing opens at once, one that lands a Big
            // Win gets the long build-up. See Games/Shared/mysteryDrama.
            const reel = reelset?.reelControllers?.[reelIndex];

            await overlay?.playRevealSequence?.(
              row,
              MYSTERY_SYMBOL_ID,
              replacementId,
              {
                anticipationClip: mysteryClipRef.current,
                // The last frame before the doors move. Any later and they
                // part onto an empty cell; any earlier is equally safe, since
                // the lid is opaque for the whole build-up.
                onOpening: () => {
                  reel?.replaceSymbolAtPosition?.(row, replacementId);
                  reel?.showSymbolTexture?.(row);
                },
              },
            );

            // ── Hand the lid back ──
            //
            // _fadeOut has already taken the host to alpha 0, so this changes
            // nothing on screen — it hides the pooled spine, parks it and puts
            // its setup pose back, ready for the next round. The revealed
            // symbol is a plain cell from here on and needs none of it.
            overlay?.releaseRevealSymbol?.(row, MYSTERY_SYMBOL_ID);
          });
        }),
      );

        // ── EVERYTHING stops before anything is processed ──
        //
        // The cells land independently and a `+FS` window takes longer than the
        // rest — that part is meant to overlap, and the coins finishing first is
        // correct. What must NOT overlap is the loop: a magnet cannot start
        // collecting while a window is still deciding what is in it.
        //
        // Vladimir, 2026-09-18: *"processing of events cant start until
        // EVERYTHING STOPS"*. So this is the seam — every landing, including
        // every open window, is joined here, and the multiplier/collector loop
        // below begins on a board that has completely settled.
        if (plusReveals.length) {
          await Promise.all(plusReveals);
        }

        // ── Then the loop, once every cell has landed ──
        //
        // Iteration 1 is the cascade above. Everything after it — a multiplier
        // firing, a collector gathering, the cleared cells respinning — happens
        // across the whole board rather than per cell, so it runs here with the
        // reveals finished and all of them still standing.
        //
        // Returns immediately for the common case of a single iteration.
        // ── The level the round was PLAYED at ──
        //
        // Read here rather than subscribed, and read NOW rather than later: the
        // server may already have moved the ladder for this spin, but that move
        // is parked in `pendingServerLevel` and is not committed until the
        // scatter charge walks it — which happens after this, after the win. So
        // `serverLevel` still holds the rung the payout was calculated at, which
        // is the one the GDD multiplies by.
        const goldenLevel = multiplierForServerLevel(
          store.getState().freeSpins.serverLevel,
        );

        await runGoldenMysteryLoop({
          round: currentRound,
          cellAt: (reel, row) =>
            reelset?.overlayReelControllers?.[reel]?.goldenCell?.(row) ?? null,
          // ── A `+FS` that lands on a RESPIN opens too ──
          //
          // The inline reveal above reads `goldenOutcomeAt`, which is the FIRST
          // patch only. A cell the collector emptied respins, and what lands in
          // it can be a `+FS` — its spins are counted either way, because
          // goldenPlusCells flattens every iteration, so the player was paid for
          // a window they watched stay shut. Vladimir, 2026-09-22: *"collect
          // collected them, and in next round we landed on golden mystery, and
          // it didnt spin."*
          //
          // The whole sequence, not a re-spin: this cell was cleared, so there
          // is no open window to turn. Its doors have to part.
          //
          // Marked in `plusRevealed` for the same reason the inline ones are —
          // the final paint would otherwise put the closed door back over it.
          onScatterDrop: (cell) => {
            if (cell.symbolId !== SCATTER_3_SYMBOL_ID) return;
            // Once per cell — see plusOpened. A `+FS` awards, so it is
            // terminal; only a cell that has never opened gets a sequence.
            const key = `${cell.reel},${cell.row}`;
            if (plusOpened.has(key)) {
              return;
            }
            const overlay = reelset?.overlayReelControllers?.[cell.reel];
            if (!overlay) return;
            plusOpened.add(key);
            const plus = overlay.playRevealSequence?.(
              cell.row,
              SCATTER_3_SYMBOL_ID,
              cell,
            );
            if (!plus) return undefined;
            // The same list, marked the same way, joined the same way — this is
            // the block above, reached from a respin instead of the landing.
            const tracked = plus
              .then((ran) => {
                if (ran) plusRevealed.add(`${cell.reel},${cell.row}`);
              })
              .catch((error) => {
                fsDiag("plus reveal failed", {
                  reel: cell.reel,
                  row: cell.row,
                  error: String(error),
                });
              });
            plusReveals.push(tracked);
            // RETURNED as well, so the loop can hold its wave for this cell —
            // the coins wait for it before the multiplier goes on.
            return tracked;
          },
          // The bonus room's own trail layer and animator — BonusGameView owns
          // them, exactly as GameView owns the base game's. Read at fire time.
          trailAnimator: levelChargingAnimatorRef?.current ?? null,
          // ── The last phase: the box claims the board ──
          //
          // x1 skips it, which is the entry rung and every base-game round.
          // The origin is resolved when it fires, not now: the box moves with a
          // resize and the feature can run for seconds.
          //
          // The BANKED box, not the filling one. `goldenLevel` above is
          // multiplierForServerLevel(serverLevel), and the box that carries that
          // number is the last one to have SHUT — `lvl_x5` closed IS x5. This
          // used to read currentBoxPosition, the box being filled, which is one
          // rung higher at every level but the ceiling: at serverLevel 4 the
          // pips came out of `lvl_x10` while x5 was applied, so a correct payout
          // looked like a shortfall. The maths never moved; only the box does.
          levelMultiplier: goldenLevel,
          levelOrigin: () =>
            freeSpinsGameViewControllerRef?.current?.bankedBoxPosition?.() ?? null,
          // This room's trace. The loop is shared and logs as `[BASE]` by
          // default, which in here reads as a base round in the bonus room.
          diag: fsDiag,
        });

        // ── And join the nested ones ──
        //
        // The join at the top of this block covers the reveals the LANDING
        // started. A `+FS` that arrived on a respin inside the loop was pushed
        // onto the same list after that join had already run, so it needs this
        // one — otherwise the board is read, and painted, while its window is
        // still deciding.
        //
        // Already-settled promises make this free on every round that had none.
        if (plusReveals.length) {
          await Promise.all(plusReveals);
        }

        // ── The picture each Golden cell keeps ──
        //
        // The feature runs on live spines and hands them back the moment the
        // next spin starts. Underneath them the reel still holds the MASKED
        // mystery texture, because a Golden cell is deliberately never given a
        // new identity — so without this the board flashes back to a row of
        // question marks and spins that away.
        //
        // Painted from the board's FINAL state, not from what first landed. A
        // cell that opened on a x2 ends as whatever respun into it once the
        // multiplier deleted itself; a coin that was multiplied and then
        // collected ends as something else again. The magnet's total and a
        // coin's multiplied value exist nowhere in the payload — they are
        // computed by goldenMysteryScript, which is what makes this possible.
        //
        // TEXTURE ONLY. The cell keeps its identity, which is what keeps the
        // ordinary win presentation off it — see the guards in
        // OverlayReelController.
        // The level multiplier goes in here too — the picture the cell keeps has
        // to be the number the round paid on, not the raw one the loop summed.
        //
        // ── Which cells STAY AWAKE when the feature hands over ──
        //
        // Not "which cells pay". A Golden cell's picture is a live spine on the
        // overlay layer, which draws above the dim, so a cell that keeps it
        // stays lit — and one that is parked is hidden AND not ticking. Three
        // sets have to survive the hand-over, and only the first is money:
        //
        //   1. The cells that PAID. goldenPayingCells.
        //
        //   2. Every cell a NON-payout win names — the trigger line's scatters,
        //      which are not money and never will be. Parking them meant they
        //      went dark the instant the feature ended and show-all woke them a
        //      beat later: a cell visibly disappearing and coming back for no
        //      reason the player can see. The feature's own payout is excluded
        //      because it carries ALL of the feature's cells, paying or not, and
        //      goldenPayingCells has already said which of those earned it.
        //
        //   3. Every cell that CHARGES THE LEVEL — `PLUS`, which awards spins
        //      and a point, and the plain `SC`, which awards only the point.
        //      The level-charge walk asks each of them to play `distribute` and
        //      fire a bolt at its box; parked, nothing shows and the clip never
        //      completes, so the walk waits on a promise that cannot settle.
        //
        // goldenLevelCells is deliberately the SAME function boxChargeCells
        // fires, not a second rule that resembles it. The set kept awake and the
        // set asked to perform cannot drift if they are one list.
        //
        // The level multiplier is not passed to goldenPayingCells: it scales
        // money cells, and scaling by a positive level cannot change whether a
        // cell pays.
        const paying = goldenPayingCells(currentRound);
        for (const win of currentRound?.wins || []) {
          if (win?.isFeaturePayout) continue;
          for (const [reel, row] of win?.positions || []) {
            paying.add(`${reel},${row}`);
          }
        }
        for (const charging of goldenLevelCells(currentRound)) {
          paying.add(`${charging.reel},${charging.row}`);
        }
        for (const cell of goldenShownBoard(currentRound, goldenLevel).values()) {
          // A `+FS` whose doors opened keeps the number it landed on; one that
          // never ran keeps the closed door. Same flag, same reason, as the
          // gamble scatter's `revealed` in the base game.
          const texture = goldenCellTexture(
            plusRevealed.has(`${cell.reel},${cell.row}`)
              ? { ...cell, revealed: true }
              : cell,
          );
          if (!texture) continue;
          const reel = reelset?.reelControllers?.[cell.reel];
          reel?.setSymbolTextureOnly?.(cell.row, MYSTERY_SYMBOL_ID, texture);
          // ── And UNCOVER it ──
          //
          // The cell's reel sprite has been at alpha 0 since the mystery
          // landed on it: an overlay spine took the cell over and
          // hideSymbolTexture put the flat picture out of the way. The
          // ordinary mystery undoes that at `onOpening` — "any later and they
          // part onto an empty cell" — but a Golden cell returns before that
          // line, because its whole presentation lives on the spine.
          //
          // setSymbolTextureOnly will not do it: it paints the texture and
          // deliberately leaves alpha alone, so "a hidden cell stays hidden".
          // So the composite was being painted onto an invisible sprite, and
          // the only thing standing between the player and an empty cell was
          // the live spine on top of it.
          //
          // That is fine for a cell that keeps its spine and fatal for one
          // that does not: parkGoldenCell hides the spine on the argument that
          // "the reel's identical composite stands in for it", and the
          // composite was not visible. Every parked cell went blank — which is
          // every scatter, every spent multiplier and every emptied collector,
          // because none of them is money and none of them is in `paying`.
          //
          // It LOOKED like it worked on a winning round only because the win
          // cycle calls showAllSymbolTextures between lines and put them back a
          // beat later. On a round that pays nothing that cycle returns at
          // `!triggerWin` and nothing ever does — so the symbols the feature
          // dropped simply vanished when the sequence ended. Mina, demo 2, and
          // Vladimir 2026-09-22: "the same symbols disappear constantly".
          //
          // The feature owns its own picture. It does not borrow the win
          // cycle's clean-up to become visible.
          reel?.showSymbolTexture?.(cell.row);
          // ── The feature hands this cell to the win cycle ──
          //
          // Every cell, paying or not: until this call nothing may park one,
          // because parking stops the spine ticking and a sequence still in
          // flight would never reach its own `complete`. See parkGoldenCell.
          //
          // A cell no win is lighting is parked in the same breath, so the
          // board reads correctly the moment the feature ends rather than at
          // the first line change.
          reelset?.overlayReelControllers?.[cell.reel]?.settleGoldenCell?.(
            cell.row,
            !paying.has(`${cell.reel},${cell.row}`),
          );
        }

      } catch (error) {
        // Named loudly: a silent catch turns a broken reveal into a board that
        // simply never opens, which is the hardest thing to find from outside.
        console.error("[Mystery] free-spins reveal failed — releasing:", error);
      } finally {
        mysteryRunningRef.current = false;
        dispatch(setCriticalAnimationPlaying(false));
        // ── The anticipation ends where the answer begins ──
        //
        // The room's copy of the base game's rule. The lamp and the reel
        // lightning say something is still being decided; by this line it is,
        // so holding them through the win would be the scene anticipating a
        // result it is already showing.
        //
        // Here rather than at any exit above, because this is where all of them
        // meet, and it must run on a FAILED reveal too — otherwise the room is
        // left lit for ever with nothing before the next spin to take it down.
        //
        // Safe for an ordinary mystery, which never held them: stopSceneAccents
        // skips a one-shot and skips a fitted accent already counting out its
        // passes.
        freeSpinsGameViewControllerRef?.current?.stopAccent();
        // The main effect waits on this, so the win presentation starts here.
        // Published even on a failure: the spin still has wins to pay, and a
        // reveal that did not finish must not stop them being presented.
        setMysteryRevealedRoundId(roundId);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    isSpinning,
    currentRound,
    bonusBoardActive,
    mysteryRevealedRoundId,
    levelChargedRoundId,
    // The arrival-board refusal reads it, and it is what turns the room from
    // scenery into a round that may present.
    freeSpinsPlayed,
    // How a reload gets its second chance: the popups closing, and the
    // per-frame retry while the room is still being built.
    introPopupActive,
    introTransitionActive,
    freeSpinsReloadActive,
    infoPopupActive,
    mysterySceneTick,
  ]);

  // ── Re-arm both sequences on every spin ──
  //
  // The guards below are "have I already presented this round". With a real
  // round id that is self-clearing; with `id: null` on every free spin it is
  // permanent, so the spin itself has to clear it. Runs on the RISING edge of
  // isSpinning, which is the one moment a new round is certainly starting and
  // certainly has nothing presented yet.
  //
  // The "already running" refs are cleared here too. They are held for the
  // whole length of an ASYNC sequence, so a spin that manages to start over one
  // — a manual press mid-reveal is the only route left — leaves the ref set for
  // ever: the next round's sequence returns at the ref, never publishes its id,
  // and the main effect waits on an id that will never come. The screen simply
  // stops. A new spin means the old sequence is moot, so the ref goes with it.
  useEffect(() => {
    if (!isSpinning) return;
    setMysteryRevealedRoundId(UNPRESENTED);
    setLevelChargedRoundId(UNPRESENTED);
    setRetriggerWinShown(false);
    mysteryRunningRef.current = false;
    levelChargingRef.current = false;
    mysterySceneWaitsRef.current = 0;
    // And every settled mystery cell goes back to the pool. The base game does
    // this in snapClearAll, which handleSpin calls before the reels start; this
    // room has no such call — freeSpinBasicSnapClearAllRef is registered and
    // nothing ever invokes it — so the rising edge is the spin start here.
    // Idempotent, and it must not be missed: a Golden cell holds a pooled spine
    // and an injected skeleton, and the reel is about to spin under it.
    executeOnOverlayReelControllersRef.current?.((oc) =>
      oc?.clearGoldenMysteries?.(),
    );
  }, [isSpinning]);

  /**
   * Main effect: Orchestrates win animations after spin completes.
   * Handles expanding wilds, normal wins, and Big Win animations.
   */
  useEffect(() => {
    // Every one of these is a silent return, and any of them means "no win
    // text, no boxes, no counter" — indistinguishable from the outside. The
    // comments that were on the individual tests are kept as the names.
    if (
      fsBlockedBy("winController.main", [
        ["not ready", !isReadyRef.current],
        ["no reelset controller", !reelsetControllerRef.current],
        ["!spinComplete", !spinComplete],
        ["isAnimatingRef (already presenting)", isAnimatingRef.current],
        [
          "animationCompletedRef (round already presented)",
          animationCompletedRef.current,
        ],
        ["introPopupActive", introPopupActive],
        // The mid-round reload popup, on exactly the principle of the intro
        // popup above: while a popup owns the screen nothing may celebrate
        // under it. The flag is in the deps, so its close re-runs this effect
        // and the restored round presents THEN.
        ["freeSpinsReloadActive", freeSpinsReloadActive],
        // The free-rounds message blocks ANY win, not only a big one — the
        // same rule as the base game's main effect. A restored round's win
        // replayed under it was a win the player closed the popup onto after
        // it had already finished.
        ["infoPopupActive", infoPopupActive],
      ])
    ) {
      return;
    }




    if (bonusBoardActive) {
      fsDiag("winController.main BLOCKED by bonusBoardActive (base game owns it)");
      return;
    }

    // ── The ARRIVAL board is scenery. Nothing on it is ours to celebrate. ──
    //
    // The room opens on a copy of the trigger spin's screen, and it exists for
    // one reason: the scatter is still standing on it, so the card can be shown
    // and its pips can fly into the level box. That spin was presented in the
    // base game, it was paid there, and its win is already inside the round
    // total the Total Win dialog will read.
    //
    // Presenting it again gives the player win boxes and symbol animations for
    // money they already collected, in a room that had nothing to do with it.
    //
    // ONE signal: `played === 0`. No free spin has resolved yet, so any win on
    // that board belongs to the base game — and arrivalBoardFrom strips `wins`
    // and `win` anyway, so the presentation returns at its own gate as well.
    //
    // NOT `isTriggerBoard` — see the mystery reveal's own note above. The
    // completion board carries that flag and MUST present.
    if (freeSpinsPlayed === 0) {
      fsDiag("winController.main BLOCKED by arrival board (base game paid it)", {
        played: freeSpinsPlayed,
        wins: currentRound?.wins?.length ?? 0,
        win: currentRound?.win ?? 0,
      });
      return;
    }

    // ── Only the mystery comes before the win ──
    //
    // The reveal decides what is ON the board, so evaluating first would
    // celebrate the wrong symbols — and the effect re-runs when the id lands,
    // which is why it is state.
    //
    // The scatter's level charge used to be a second gate here. It is now the
    // other way round: the charge waits on THIS presentation, because the win
    // is the round's result and the charge is what the result leads to.
    if (
      mysteryTargetsByReel(currentRound).length &&
      mysteryRevealedRoundId !== (currentRound?.id ?? null)
    ) {
      fsDiag("winController.main BLOCKED by mystery reveal in progress");
      return;
    }

    fsDiag("winController.main -> presenting", {
      roundId: currentRound?.id,
      win: currentRound?.win,
      wins: currentRound?.wins?.length ?? 0,
      triggerWin: currentRound?.triggerWin,
      hasPaylineTable: Array.isArray(winningLines),
    });

    if (!currentRound?.triggerWin || !winningLines) {
      // A silent exit until now, and it is the FIRST thing a missing
      // presentation hits. Both halves matter: `triggerWin` is the round's own
      // flag, `winningLines` is the paytable, and a reload can be missing
      // either.
      fsDiag("winController.main -> NO-WIN path", {
        triggerWin: currentRound?.triggerWin ?? null,
        hasPaylineTable: Array.isArray(winningLines),
        win: currentRound?.win ?? null,
      });

      // On no-win rounds, wait for any in-flight POT charging bolts to complete
      // before allowing autoplay to fire the next spin. Without this, autoplay
      // starts the next spin while bolts are mid-charge, canceling them visually.
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        const animator = potChargingAnimatorRef.current;
        if (animator._activeBolts.length > 0) {
          dispatch(setCriticalAnimationPlaying(true));
          animator.waitForAllTrails().then(() => {
            dispatch(setCriticalAnimationPlaying(false));
          });
        }
      }
      return;
    }

    const winTypeLocal = winType;
    const isAnimatedWinLocal = isAnimatedWin;
    const hasScatterWinLocal = hasScatterWin;

    const expandingWildReels =
      FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && !hasScatterWinLocal
        ? currentRound.expandingWildReels || []
        : [];
    const hasExpandingWilds = expandingWildReels.length > 0;

    const expandingWildReelIndices = expandingWildReels.map(
      ({ reelIndex }) => reelIndex,
    );
    expandingWildReelIndicesRef.current = expandingWildReelIndices;

    const wins = currentRound?.wins;
    const winsHash =
      Array.isArray(wins) && wins.length > 0 ? JSON.stringify(wins) : null;
    const roundId = currentRound?.id || "unknown";
    const uniqueAnimationId = `${roundId}-${winsHash}`;

    if (animatedRoundsRef.current.has(uniqueAnimationId)) {
      // Mark animation as completed to unblock UI
      animationCompletedRef.current = true;
      return;
    }

    animatedRoundsRef.current.add(uniqueAnimationId);

    // Lock immediately to prevent effect re-entry during TickerTimeout delay
    isAnimatingRef.current = true;


    // FREE SPIN TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (3+ scatters)
    if (FEATURES_CONFIG.FREE_SPINS.ENABLED && freeSpinTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ROYAL COLLECT TRIGGER: Disable entire UI immediately so user can't interact
    // during win processing of a trigger round (2 coins + 1 royal)
    if (FEATURES_CONFIG.ROYAL_COLLECT.ENABLED && royalCollectTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // POT COLLECTION TRIGGER: Same pattern — disable UI during trigger wins
    if (FEATURES_CONFIG.POT_COLLECTION.ENABLED && potCollectionTriggered) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // HOLD & WIN TRIGGER (a free spin landed 6+ coins): the base game's lock
    // (useWinController) — free spins are the base game without the bet. It
    // also holds the free-spins loop until the transition into H&W.
    if (selectHoldAndWinIntroPending(store.getState())) {
      dispatch(setUserInterfaceDisabled(true));
      dispatch(setCriticalAnimationPlaying(true));
    }

    // ═══════════════════════════════════════════════════════════════════════════
    // TURBO WIN FAST PATH (HOLD OR AUTOPLAY)
    // Activates when user is actively holding spacebar/button (turbo or super turbo)
    // OR autoplay is running in super turbo mode.
    // Single-press in super turbo mode falls through to normal win processing below.
    // Skip all win animations for regular wins — show value, unblock spin immediately.
    // Animated wins (Big Win, Mega Win) and feature triggers still go through normal pipeline.
    // ═══════════════════════════════════════════════════════════════════════════
    const currentSpeed = store.getState().settings.autoPlaySpeed;
    // Read from REF (synchronous) — React state can be stale if user released
    // spacebar between spin-fire and win-processing. The ref updates instantly.
    const isActivelyHolding = spacebarTurboActiveRef.current;
    // const isAutoPlaySuperTurbo =
    //   store.getState().settings.autoPlayActive && currentSpeed === "super_turbo";
    // The free-spins round runs ITSELF — it is autoplay in all but name (the
    // base autoplay is paused for it), so super turbo here is "autoplay in
    // super turbo" (2026-10-23: turbo in free spins as in the base game).
    const isAutoPlaySuperTurbo = currentSpeed === "super_turbo";


    // ── A TRIGGER STOPS SUPER TURBO HERE TOO ──
    //
    // useWinController's own guard, ported. Two faults in the old test, both
    // of them latent here for the same reasons they were latent there:
    //
    //   stale flags   the three `*Triggered` values above come off useSelector,
    //                 so on the tick the trigger is dispatched they can still
    //                 read false while the store already says true. Read from
    //                 the store instead and the answer is the current one.
    //
    //   no reveal     a round can carry `triggerReveal` before any slice flag
    //                 is raised, and the fast path would take it past without
    //                 ever presenting it. Same test as isRevealRound below,
    //                 inlined because that helper is declared further down.
    //
    // GDD I14 swaps SC/SC1 for PLUS scatters in the bonus, so this room may
    // never actually see a trigger round — in which case the guard is inert,
    // which is the right way for it to fail. Every other speed already stops.
    const triggerState = store.getState();
    // const isTriggerRoundNow =
    //   currentRound?.triggerReveal === true ||
    //   !!triggerState.freeSpins?.triggered ||
    //   !!triggerState.featureGame?.royalCollectTriggered ||
    //   !!triggerState.potCollection?.potCollectionTriggered ||
    //   freeSpinTriggered ||
    //   royalCollectTriggered ||
    //   potCollectionTriggered;
    // ── ...and this room's OWN triggers (QA, 2026-10-28: "Game freezes when
    // additional Free Spins or Hold & Win are triggered in Super Turbo") ──
    // A retrigger opens its popup only after its scatter line has played
    // (retriggerWinShown), and a Hold & Win trigger hands over to its intro
    // from the presentation — the fast path skipped both, with the UI already
    // locked above, and the round waited for ever. The base game's guard has
    // the Hold & Win test; the retrigger is this room's alone.
    const isTriggerRoundNow =
      currentRound?.triggerReveal === true ||
      !!triggerState.freeSpins?.triggered ||
      !!triggerState.featureGame?.royalCollectTriggered ||
      !!triggerState.potCollection?.potCollectionTriggered ||
      selectHoldAndWinIntroPending(triggerState) ||
      currentRound?.freeSpinsRetrigger > 0 ||
      freeSpinTriggered ||
      royalCollectTriggered ||
      potCollectionTriggered;

    if (
      currentSpeed === "super_turbo" &&
      (isActivelyHolding || isAutoPlaySuperTurbo) &&
      !isAnimatedWinLocal &&
      !isTriggerRoundNow &&
      !hasExpandingWilds
    ) {
      // ─────────────────────────────────────────────────────────────────────
      // SUPER TURBO FAST PATH — skip animations, show value, unblock spin.
      //
      // RACE CONDITION SAFETY: The user's keyup event may still be in transit
      // (finger physically leaving the key). If they release within ~100ms,
      // the Recovery Effect below detects the spacebarTurboActive→false
      // transition, undoes this fast path, and runs normal win processing.
      // ─────────────────────────────────────────────────────────────────────

      // Signal WinDisplay to show the final value immediately
      dispatch(setCounterAnimationStarted(true));
      dispatch(setCounterAnimationFinished(true));
      dispatch(setCriticalAnimationPlaying(false));

      // Flash WinText on the reelset (both spacebar hold and autoplay super turbo)
      if (
        FEATURES_CONFIG.WIN_LINE_DIGIT.ENABLED &&
        globalLineWinTextRef?.current &&
        currentRound?.win
      ) {
        globalLineWinTextRef.current.showWinAmount(currentRound.win, {
          // skipIntro: false,
          // targetDuration: 700,
          // A FLASH, not a fast-forward (2026-10-08): shown at once, burning
          // at its own speed, faded out by the end of the same 700 ms — see
          // WinTextController.showWinAmount `flashMs`.
          flashMs: 700,
        });
      }

      isAnimatingRef.current = false;
      animationCompletedRef.current = true;

      // Tag this round for potential recovery if user releases right after
      fastPathRoundRef.current = uniqueAnimationId;


      return () => clearAllTimeouts();
    }



    // CRITICAL: Block the spin button IMMEDIATELY, before the wait below
    // Without this, there's a gap where counterAnimationStarted is false
    // and the user can click spin before win processing starts
    dispatch(setCounterAnimationStarted(true));
    // Also hold WinDisplay hidden during the wait — it checks !criticalAnimationPlaying,
    // so it won't show until setCriticalAnimationPlaying(false) fires inside the body
    dispatch(setCriticalAnimationPlaying(true));

    // ── WAIT FOR THE BOARD TO BE STILL — the base game's mechanism ──
    //
    // This was a flat FREE_SPIN_WIN_LINES_DELAY of 400ms. The landing bounces
    // are 367-667ms, so it was shorter than most of them and the win
    // presentation — which takes the same track 0 the bounce plays on — cut the
    // last reel's landing off. Same fault as the base game had at 250ms, and
    // the same fix, sharing one helper so the two rooms cannot drift.
    //
    // Costs nothing when nothing is landing: a board with no landing clips, or
    // turbo with landings switched off, resolves on the next microtask.
    const settleWait = () =>
      waitForBoardToSettle(
        reelsetControllerRef.current?.overlayReelControllers,
        {
          fallbackMs:
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.FREE_SPIN_WIN_LINES_DELAY,
        },
      );

    settleWait().then(async () => {
      // Wait for POT charging trails to finish before processing wins
      if (FEATURES_CONFIG.POT_CHARGING.ENABLED && potChargingAnimatorRef?.current) {
        await potChargingAnimatorRef.current.waitForAllTrails();
      }

      if (hasExpandingWilds) {
        dispatch(setExpandingWildsOccurred(true));
        dispatch(setCriticalAnimationPlaying(true));

        // All expanding wilds play the same sound
        soundManager.play(SOUND_NAME.expanding_wild);

        const animationMode = FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE;

        (async () => {
          let animationPromises = [];

          if (animationMode === "full_reel") {
            animationPromises = expandingWildReels.map(({ reelIndex }) =>
              playFullReelExpandingWild(reelIndex, reelRefs, overlayReelRefs),
            );
          } else if (animationMode === "cascade") {
            animationPromises = expandingWildReels.map(
              ({ reelIndex, wildPosition }) =>
                playCascadingExpandingWild(
                  reelIndex,
                  wildPosition,
                  reelRefs,
                  overlayReelRefs,
                  currentTimeoutsRef,
                  dispatch,
                ),
            );
          }

          // Wait for all expanding wild "appear" animations to complete (event-driven)
          if (animationPromises.length > 0) {
            await Promise.all(animationPromises);
          }

          // Hide cascade animations if needed
          if (animationMode === "cascade") {
            executeOnOverlayReelControllersRef.current(
              (overlayReelController) => {
                overlayReelController?.hideAllAnimations();
              },
            );
          }

          dispatch(setCriticalAnimationPlaying(false));

          const winningData =
            reelsetControllerRef.current.matchHorizontalWinningLines(
              winningLines,
              currentRound.wins,
              currentRound,
            );

          if (winningData && Object.keys(winningData).length > 0) {
            dispatch(setIsAnimatedWin(isAnimatedWinLocal));
            dispatch(setCounterAnimationStarted(true));

            if (FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (winTypeLocal && !winSoundPlayedRef.current) {
                if (isAnimatedWinLocal) {
                  winSoundPlayedRef.current = true;
                }
              }

              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }

              const shouldProceed = await showAllWinningSymbolsOnceRef.current(
                winningData,
                true,
                expandingWildReelIndices,
                winTypeLocal,
                isAnimatedWinLocal,
              );

              if (
                shouldProceed &&
                (!spacebarTurboActiveRef.current || isRevealRound())
              ) {
                startWinningLinesAnimationRef.current(winningData);
              } else {
                if (
                  FEATURES_CONFIG.WIN_LINES.ENABLED &&
                  winLinesContainerRef.current
                ) {
                  winLinesContainerRef.current.hideWinLine();
                }
                if (globalLineWinTextRef?.current) {
                  globalLineWinTextRef.current.hide();
                }
                // NEW - unified line win text
                if (lineWinTextRef?.current) {
                  lineWinTextRef.current.hide();
                }
              }
            }

            if (!FEATURES_CONFIG.EXPANDING_WILDS.SHOW_ALL_WINS_BEFORE_CYCLING) {
              if (isAnimatedWinLocal) {
                dispatch(closeAllPopups());
                dispatch(setSymbolPopupBlocked(true));
                dispatch(setWinAnimationsPlaying(true));
              }
            }
          }
        })();
      } else {
        const winningData =
          reelsetControllerRef.current.matchHorizontalWinningLines(
            winningLines,
            currentRound.wins,
            currentRound,
          );

        if (!winningData || Object.keys(winningData).length === 0) {
          // The other silent exit. `wins` had entries but none of them
          // resolved — the shape the provider sent is not the shape
          // matchHorizontalWinningLines expects, so the round pays nothing with
          // nothing said. Reported with the raw entry so the shape is visible.
          fsDiag("winController.main -> NOTHING MATCHED", {
            wins: currentRound?.wins?.length ?? 0,
            win: currentRound?.win ?? null,
            first: JSON.stringify(currentRound?.wins?.[0] ?? null),
          });
          // ── RELEASE THE LOCK. This return is a dead end otherwise ──
          //
          // criticalAnimationPlaying was raised well above, before the timer
          // that holds both branches, and this exit is the one path out of the
          // effect that never reached the release. The free-spin loop blocks on
          // that flag and handleResume refuses on it, so leaking it here kills
          // the round outright: no next spin, no resume, no outro — only another
          // reload, which lands in the same place.
          //
          // Newly reachable, and by my own change: a restored mid-round free
          // spin used to stop at `!currentRound.triggerWin` long before the
          // lock was taken, and now it carries a real win and gets this far. So
          // a provider whose `wins` the parser cannot resolve would have traded
          // a silent no-payout for a bricked round.
          //
          // Also reset the presentation refs, so the round is not left marked
          // as "presenting" — the sibling branch's early exits do the same.
          isAnimatingRef.current = false;
          animationCompletedRef.current = true;
          dispatch(setCriticalAnimationPlaying(false));
          return;
        }

        dispatch(setCriticalAnimationPlaying(true));

        const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
        const isScatterWin = currentRound?.type === "feature";
        const skipShowAllWins =
          isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

        // Skip show-all when the only win(s) are trigger symbols (coins/scatters).
        // No point showing all + holding when we can go straight to cycling the trigger line.
        // This avoids the visual jitter from short trigger animations (coins) looping only once
        // within the wild-based display window.
        const lineIds = Object.keys(winningData);
        const isTriggerOnly = lineIds.length > 0 && lineIds.every((lineId) => {
          const data = winningData[lineId];
          return data.isCoin || data.isScatter;
        });
        const skipShowAllForTriggerOnly = shouldShowAllWins && isTriggerOnly;

        (async () => {

          dispatch(setCriticalAnimationPlaying(false));
          dispatch(setIsAnimatedWin(isAnimatedWinLocal));
          dispatch(setCounterAnimationStarted(true));

          // Trigger character "yes" animation on every win
          if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
            characterAnimationControllerRef.current?.playAnimation("yes");
          }

          // ── NO RAT REACTION IN THIS ROOM ──
          //
          // Client feedback, demo 1: "Rat should be IDLE in Bonus game" —
          // "Player's focus must be kept on the reel".
          //
          // This block used to mirror the base game exactly: BIG wins got
          // `rat_shocked2`, every other paid win `rat_shocked`, held for as long
          // as the win was on screen. The rig is shared and byte-identical
          // between the two rooms, so it worked here for free — and that is
          // precisely what they do not want. In the bonus room the scene holds
          // its plain `idle` through every win.
          //
          // The BASE game is unchanged and still reacts — see useWinController.
          // Only this room is quiet.
          //
          // The LAMP is deliberately left alone. They named the rat, and the
          // lamp is a different accent on a different moment (the mystery
          // wait); `reel_highlight` too, which flashes across the reel frame and
          // so pulls the eye TOWARD the board rather than away from it.
          //
          // stopAccent on the next spin's cleanup stays as it is: the MAIN track
          // is shared with the lamp, which still uses it.

          let shouldProceed = true;

          if (shouldShowAllWins && !skipShowAllWins && !skipShowAllForTriggerOnly) {
            if (winTypeLocal && !winSoundPlayedRef.current) {
              if (isAnimatedWinLocal) {
                winSoundPlayedRef.current = true;
              }
            }

            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }

            shouldProceed = await showAllWinningSymbolsOnceRef.current(
              winningData,
              false,
              [],
              winTypeLocal,
              isAnimatedWinLocal,
            );
          }


          if (shouldProceed) {
            // When show-all is skipped, release the animation lock manually
            // (showAllWinningSymbolsOnce normally does this at its end)
            if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
              isAnimatingRef.current = false;
            }

            // ── A CHARGING SCATTER GETS THE BOARD TO ITSELF ──
            //
            // A round can both pay and land a scatter that fills a level box,
            // and those are two separate statements: "you won this much", then
            // "and that scatter moves you up". They were being made at once —
            // the line cycle kept running, boxes and lines and dim still up,
            // while the scatter fired its bolt across them. Two things moving
            // for two different reasons, and neither reads.
            //
            // So the paying half ends at the GLOBAL win. The show-all has just
            // finished, every winning symbol has been seen together, and that
            // is the whole payment statement. Then the board is cleared and the
            // scatter is the only thing left moving: its own win animation, its
            // trail, the pip landing, the box closing.
            //
            // Read through boxChargeCells, the same rule the walk itself fires
            // on, so "the lines stood down" and "a bolt flew" can never
            // disagree. From the STORE rather than the closure: pendingTier is
            // parked by applyFsState when the response lands, and this runs
            // inside a timer that was armed before that.
            const chargeWillRun =
              boxChargeCells(
                currentRound,
                store.getState().freeSpins?.pendingTier ?? null,
              ).length > 0;

            // During turbo/autoplay: skip per-line cycling — show-all is sufficient.
            // Autoplay continuation will snap-clear and fire next spin.
            // ...unless the round carries a reveal — see isRevealRound.
            //
            // A charging round takes the same road as turbo, for a different
            // reason: not "the machine is in a hurry" but "something else owns
            // the next beat".
            if ((!turboActiveRef.current || isRevealRound()) && !chargeWillRun) {
              startWinningLinesAnimationRef.current(winningData);
            } else {
              // Clean up show-all visuals so autoplay can proceed cleanly
              if (
                FEATURES_CONFIG.WIN_LINES.ENABLED &&
                winLinesContainerRef.current
              ) {
                winLinesContainerRef.current.hideWinLine();
              }
              if (globalLineWinTextRef?.current) {
                globalLineWinTextRef.current.hide();
              }
              if (lineWinTextRef?.current) {
                lineWinTextRef.current.hide();
              }

              // ── Boxes and dim, for the CHARGE case only ──
              //
              // Turbo does not need these: autoplay's snapClearAll runs a beat
              // later and takes them. A charging round has no such spin coming
              // — the walk owns the next second or two — so anything left up
              // stays up, framing symbols that are no longer the subject.
              //
              // Deliberately NOT resetAllEffects: that restores every symbol
              // texture, which would repaint a revealed mystery back to its
              // masked picture. Only what the win put up comes down.
              if (chargeWillRun) {
                if (
                  FEATURES_CONFIG.WIN_BOX.ENABLED &&
                  winBoxContainerRef.current
                ) {
                  winBoxContainerRef.current.hideWinBoxes();
                }
                // Undim, so the scatter's bolt is not crossing a darkened board.
                if (dimReelSetRef.current?.snapClear) {
                  dimReelSetRef.current.snapClear();
                }
              }
            }
          } else {
            if (
              FEATURES_CONFIG.WIN_LINES.ENABLED &&
              winLinesContainerRef.current
            ) {
              winLinesContainerRef.current.hideWinLine();
            }
            if (globalLineWinTextRef?.current) {
              globalLineWinTextRef.current.hide();
            }
            // NEW - unified line win text
            if (lineWinTextRef?.current) {
              lineWinTextRef.current.hide();
            }
          }

          if (!shouldShowAllWins || skipShowAllWins || skipShowAllForTriggerOnly) {
            // ── Play win sound for non-animated wins when show-all is skipped ──
            //
            // THIS is the path a scatter trigger takes: `isTriggerOnly` makes
            // the global show-all skip entirely, so the gate there never runs.
            //
            // NOT withheld any more. The chime was deferred to the end of the
            // reveal back when it was the only sound in the sequence. The doors
            // have their own sound now, so the order is simply played in order:
            // the regular symbols win FIRST, on its own, and the reveal cuts it
            // when the doors open — see `cutSound` in gambleScatterConfig.
            // The Golden Mystery's own chime when the feature paid, the
            // ordinary one otherwise — resolved from the data, not a flag.
            // The feature runs in the room too, so this hook needs the same
            // swap the base game's does.
            if (!isAnimatedWinLocal) {
              const winChime = goldenWinSound(winningData);
              if (winChime) soundManager.play(winChime);
            }
            if (isAnimatedWinLocal) {
              dispatch(closeAllPopups());
              dispatch(setSymbolPopupBlocked(true));
              dispatch(setWinAnimationsPlaying(true));
            }
          }
        })();
      }
    });

    return () => {
      // Only clear timeouts when cycling is NOT in progress.
      // When infoPopupActive changes (info popup close), React re-runs this effect.
      // If cycling IS running (isAnimatingRef.current = true), clearing would kill it.
      // startWinningLinesAnimation already calls clearAllTimeouts on entry,
      // so skipping here is always safe for new spin starts.
      if (!isAnimatingRef.current) {
        clearAllTimeouts();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    spinComplete,
    currentRound,
    winningLines,
    reelRefs,
    overlayReelRefs,
    isSpinning,
    dispatch,
    introPopupActive,
    infoPopupActive,
    freeSpinsReloadActive,
    // The two gates above wait on these. State, not refs, precisely so the
    // presentation re-runs when each sequence finishes.
    mysteryRevealedRoundId,
    levelChargedRoundId,
    // The arrival gate: this flips to 1 with the first free spin's own board.
    freeSpinsPlayed,
  ]);

  // ═══════════════════════════════════════════════════════════════════════════
  // FAST PATH RECOVERY EFFECT
  //
  // Watches spacebarTurboActive transitions from true→false. If a fast path
  // was committed for the current round (fastPathRoundRef is set), this means
  // the user released spacebar AFTER the fast path already skipped animations.
  //
  // Recovery:
  // 1. Undo the fast path flags (animationCompleted, animatedRounds dedup)
  // 2. Reset counter animation states
  // 3. Run normal win processing with full animations
  // ═══════════════════════════════════════════════════════════════════════════
  useEffect(() => {
    // Only act on deactivation: spacebar released OR autoplay ended.
    // If BOTH are still active, no recovery needed — fast path is valid.
    if (spacebarTurboActive || autoPlayActive) {
      return;
    }

    // Check if a fast path was committed for a round that hasn't advanced
    const recoveryTag = fastPathRoundRef.current;
    if (!recoveryTag) {
      return;
    }

    // Clear the tag immediately to prevent double-recovery
    fastPathRoundRef.current = null;

    // Only recover if we're not already spinning the next round
    if (isSpinning) {
      return;
    }


    // 1. Undo dedup so the round can be re-processed
    animatedRoundsRef.current.delete(recoveryTag);
    animationCompletedRef.current = false;
    isAnimatingRef.current = true; // Lock to prevent race with main effect

    // 2. Reset counter states that the fast path set
    dispatch(setCounterAnimationStarted(false));
    dispatch(setCounterAnimationFinished(false));

    // 3. Hide the fast-path WinText flash
    if (globalLineWinTextRef?.current) {
      globalLineWinTextRef.current.hide();
    }

    // 4. Run normal win processing
    if (!currentRound?.wins || !winningLines) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winningData =
      reelsetControllerRef.current.matchHorizontalWinningLines(
        winningLines,
        currentRound.wins,
        currentRound,
      );

    if (!winningData || Object.keys(winningData).length === 0) {
      isAnimatingRef.current = false;
      animationCompletedRef.current = true;
      return;
    }

    const winTypeLocal = currentRound?.winType || null;
    const isAnimatedWinLocal = currentRound?.isAnimatedWin || false;

    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setIsAnimatedWin(isAnimatedWinLocal));
    dispatch(setCounterAnimationStarted(true));

    if (FEATURES_CONFIG.CHARACTER_ANIMATION.ENABLED) {
      characterAnimationControllerRef.current?.playAnimation("yes");
    }

    const shouldShowAllWins = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED;
    const isScatterWin = currentRound?.type === "feature";
    const skipShowAllWins =
      isScatterWin && FEATURES_CONFIG.SHOW_ALL_WINS.EXCLUDE_SCATTER;

    (async () => {
      let shouldProceed = true;

      if (shouldShowAllWins && !skipShowAllWins) {
        if (winTypeLocal && !winSoundPlayedRef.current) {
          if (isAnimatedWinLocal) {
            winSoundPlayedRef.current = true;
          }
        }

        if (isAnimatedWinLocal) {
          dispatch(closeAllPopups());
          dispatch(setSymbolPopupBlocked(true));
          dispatch(setWinAnimationsPlaying(true));
        }

        shouldProceed = await showAllWinningSymbolsOnceRef.current(
          winningData,
          false,
          [],
          winTypeLocal,
          isAnimatedWinLocal,
        );
      }

      if (shouldProceed) {
        if (!shouldShowAllWins || skipShowAllWins) {
          isAnimatingRef.current = false;
        }
        startWinningLinesAnimationRef.current(winningData);
      } else {
        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current
        ) {
          winLinesContainerRef.current.hideWinLine();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.hide();
        }
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.hide();
        }
      }

      if (!shouldShowAllWins || skipShowAllWins) {
        dispatch(setCounterAnimationFinished(true));
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spacebarTurboActive, autoPlayActive]);

  /**
   * Executes a callback function on each reel controller.
   */
  const executeOnReelControllers = useCallback(
    (callback) => {
      const reels = reelRefs.current;
      if (!reels) return;

      for (let i = 0; i < reels.length; i++) {
        const controller = reels[i]?.getReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [reelRefs],
  );

  /**
   * Executes a callback function on each overlay reel controller.
   */
  const executeOnOverlayReelControllers = useCallback(
    (callback) => {
      const overlayReels = overlayReelRefs.current;
      if (!overlayReels) return;

      for (let i = 0; i < overlayReels.length; i++) {
        const controller = overlayReels[i]?.getOverlayReelController();
        if (controller) {
          callback(controller, i);
        }
      }
    },
    [overlayReelRefs],
  );

  useEffect(() => {
    executeOnReelControllersRef.current = executeOnReelControllers;
    executeOnOverlayReelControllersRef.current =
      executeOnOverlayReelControllers;
  }, [executeOnReelControllers, executeOnOverlayReelControllers]);

  /**
   * Resets all visual effects and animations to their default state.
   * @param {boolean} instant - If true, quick sequential fade before cleanup (used on spin start)
   */
  const resetAllEffects = useCallback(async (instant = false) => {
    // Step 1: Fade dim overlay FIRST — must be gone before we mutate layers underneath
    if (dimReelSetRef.current) {
      await dimReelSetRef.current.undimReels(instant);
    }

    // Step 2: Now safe to clear win overlays and restore textures
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    executeOnOverlayReelControllersRef.current((overlayReelController) => {
      if (overlayReelController) {
        if (overlayReelController.hideAllAnimations) {
          overlayReelController.hideAllAnimations();
        }

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          overlayReelController.isExpandingWildVisible
        ) {
          overlayReelController.forceStopExpandingWildFullReel?.();
        }
      }
    });

    executeOnReelControllersRef.current((controller) => {
      if (controller && controller.showAllSymbolTextures) {
        controller.showAllSymbolTextures();
      }
    });

    // NEW - unified line win text
    if (lineWinTextRef?.current) {
      lineWinTextRef.current.hide();
    }

    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // Clear previous win positions for diff tracking
    prevWinPosSetRef.current = null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [globalLineWinTextRef, lineWinTextRef]);

  /**
   * Matches winning line data with game layout winning lines.
   * Groups multiple wins on the same line together and returns structured data for animation.
   */
  const matchHorizontalWinningLines = useCallback((winningLines, wins, round) => {
    const result = {};
    if (!Array.isArray(wins)) {
      return result;
    }

    // The room's twin of the base game's filter — the Golden Mystery runs in
    // here too, and its payout carries every cell it used. See the long note on
    // the base game's copy in useWinController.
    //
    // The level multiplier is not passed: it scales money cells, and scaling by
    // a positive level can never turn a zero into a payment or a payment into a
    // zero. What pays is the same set at every level.
    let payingCells;
    const payingCellsFor = () => {
      if (payingCells === undefined) payingCells = goldenPayingCells(round);
      return payingCells;
    };

    for (let i = 0; i < wins.length; i++) {
      const win = wins[i];
      const { id, symbol, win: winAmount } = win;
      let { positions } = win;

      if (!Array.isArray(positions)) {
        continue;
      }

      if (win.isFeaturePayout) {
        const paying = payingCellsFor();
        if (paying.size > 0) {
          positions = positions.filter(([reel, row]) =>
            paying.has(`${reel},${row}`),
          );
        }
      }

      if (!result[i]) {
        result[i] = {
          line: id != null ? winningLines[id] : null, // Scatter wins have no line (id is null)
          positions: [],
          symbol,
          win: winAmount,
          lineId: id, // Preserve original line index for win_line spine animation
          isScatter: !!win.isScatter, // Preserve scatter flag from RGS
          isCoin: !!win.isCoin, // Preserve coin trigger flag from RGS
          // ── Was READ here and never SET ──
          //
          // The win-box step below skips a feature payout on this flag, and
          // the base game's copy of this function has always carried it. This
          // one did not, so the Golden Mystery's coins were boxed in the room
          // and bare in the base game — the same feature, presented two ways.
          isFeaturePayout: !!win.isFeaturePayout,
        };
      }
      result[i].positions.push(...positions);
    }

    return result;
  }, []);

  /**
   * Syncs refs with current state values for use in async operations.
   */
  useEffect(() => {
    spinCompleteRef.current = spinComplete;
    currentRoundRef.current = currentRound;
    winningLinesRef.current = winningLines;
    dispatchRef.current = dispatch;

    turboActiveRef.current = spacebarTurboActive || autoPlayActive;
    infoPopupActiveRef.current = infoPopupActive;
  }, [
    spinComplete,
    currentRound,
    winningLines,
    dispatch,

    spacebarTurboActive,
    autoPlayActive,
    infoPopupActive,
  ]);

  /**
   * Initializes the reelset controller object once on component mount.
   * Creates lazy-loaded getters for controllers and attaches utility methods.
   */
  useEffect(() => {
    if (reelsetControllerRef.current) {
      return;
    }

    reelsetControllerRef.current = {
      _reelControllers: null,
      _overlayReelControllers: null,

      // ── Cached, but NEVER across a mount, and never while empty ──
      //
      // Both boards are mount-gated on `freeSpinsActive` (see BonusGameView),
      // so they are DESTROYED when the room closes and rebuilt from scratch
      // when it opens again. This object is created once for the session, so a
      // cache taken during the first bonus pointed at dead controllers for
      // every bonus after it — the second room drew its reels, its boxes and
      // its win text (all different refs) while every symbol animation and
      // every Golden Mystery was played into a destroyed object. Invalidated
      // on the flag below.
      //
      // Refusing to cache an EMPTY result matters just as much: the flag flips
      // a frame or two before the refs are populated, so the first read after
      // an invalidation can legitimately find nothing, and caching that would
      // brick the room just as thoroughly.
      //
      // And a PARTIAL result is refused for the same reason — see the note on
      // the base game's twin in useWinController: a list cached before the
      // last reel had built froze with a hole in it, and the reel it was
      // missing could never be reached again.
      get reelControllers() {
        if (!this._reelControllers?.length) {
          const all =
            reelRefs.current?.map((reel) => reel?.getReelController()) || [];
          const found = all.filter(Boolean);
          if (!all.length || found.length !== all.length) return found;
          this._reelControllers = found;
        }
        return this._reelControllers;
      },

      get overlayReelControllers() {
        if (!this._overlayReelControllers?.length) {
          const all =
            overlayReelRefs.current?.map((overlayReel) =>
              overlayReel?.getOverlayReelController(),
            ) || [];
          const found = all.filter(Boolean);
          if (!all.length || found.length !== all.length) return found;
          this._overlayReelControllers = found;
        }
        return this._overlayReelControllers;
      },

      executeOnReelControllers,
      executeOnOverlayReelControllers,
      resetAllEffects,
      matchHorizontalWinningLines,

      /**
       * Synchronous full visual cleanup — everything in ONE frame, no async.
       * Called by handleSpin before reels start so no previous-win artifacts remain.
       */
      snapClearAll: () => {
        // Kill async win cycling loop — prevents re-dimming after clear
        isAnimatingRef.current = false;
        // Dim overlay
        if (dimReelSetRef.current?.snapClear) {
          dimReelSetRef.current.snapClear();
        }
        // Win lines
        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }
        // Win boxes
        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
        // Overlay reel animations
        executeOnOverlayReelControllersRef.current((oc) => {
          if (oc?.hideAllAnimations) oc.hideAllAnimations();
          // Golden cells are the one thing hideAllAnimations deliberately only
          // PARKS — it has no way to know the round is over. This is that
          // moment: the board is about to become something else and the reel's
          // composite override goes with it.
          oc?.clearGoldenMysteries?.();
          if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
            oc.forceStopExpandingWildFullReel?.();
          }
        });
        // Restore symbol textures
        executeOnReelControllersRef.current((c) => {
          if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
        });
        // Line win text (per-line, not the global total)
        if (lineWinTextRef?.current) lineWinTextRef.current.hide();
        // NOTE: globalLineWinTextRef is intentionally NOT cleared here.
        // It self-cleans via its spine "out" animation → _finalizeOut().
        // This allows WinText to persist into the next spin (overlapping reels),
        // matching reference game behavior. If a new win arrives before "out"
        // finishes, showWinAmount() replaces it seamlessly.
        // Win position tracking
        prevWinPosSetRef.current = null;
      },
    };

    isReadyRef.current = true;

    // Register the synchronous cleanup on the shared ref so
    // ReelControlContext.handleSpin can call it before starting reels.
    snapClearAllRef.current = reelsetControllerRef.current.snapClearAll;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Drop the cached controllers whenever the room opens or closes ──
  //
  // `reelsetControllerRef` is built once and lives for the session, but the
  // boards it points at do not: they are mounted and unmounted with
  // `freeSpinsActive`. Without this, the SECOND bonus of a session animates
  // nothing — see the getters above.
  //
  // Both edges, not just the open one. Clearing on close as well means nothing
  // is holding a destroyed controller in the gap between rooms.
  useEffect(() => {
    const reelset = reelsetControllerRef.current;
    if (!reelset) return;
    reelset._reelControllers = null;
    reelset._overlayReelControllers = null;
  }, [bonusBoardActive]);

  /**
   * Cycles through winning lines one by one, displaying animations and win amounts.
   * Shows win text, restores textures for non-winning symbols, and plays animations for winning symbols.
   */
  /**
   * Does this round have a reveal for the line cycle to run?
   *
   * The free-spins twin of useWinController's isFeatureTriggerRound, and it
   * exists for the same reason: turbo skips per-line cycling on the argument
   * that show-all said everything, but the cycle is the only place a reveal is
   * launched. Skipping it strands the round holding criticalAnimationPlaying.
   *
   * `triggerReveal` is stamped by the provider on rounds that carry one. GDD
   * I14 replaces SC/SC1 with PLUS scatters in the bonus, so this may never be
   * true in practice — in which case the guard is simply inert, which is the
   * right way for it to fail.
   */
  const isRevealRound = useCallback(
    () => currentRoundRef.current?.triggerReveal === true,
    [],
  );

  const cycleThroughLines = useCallback(
    async (
      winningData,
      lineIds,
      isFirstEverCycle = false,
      startIndex = 0,
      onFirstLineComplete = null,
    ) => {
      // ── Did this pass PLAY anything? ──
      //
      // The twin of useWinController's flag, and here for the same reason.
      // Every `continue` below leaves the loop without awaiting, so a pass can
      // finish in zero time; runSeamlessAnimationCycle repeats this call while
      // isAnimatingRef is set, and a pass that never awaits turns that into a
      // tight loop that starves the frame — the tab locks with nothing in the
      // console, because nothing ever yields to the renderer.
      //
      // The base game reached that state through a blank gamble scatter, whose
      // spent entry it skips. This controller has no such skip today, so it is
      // insurance rather than a live fault — but the two controllers must not
      // differ on something that can hang the game, and a free-spins round is
      // the harder one to reproduce.
      let playedAny = false;

      for (let index = startIndex; index < lineIds.length; index++) {
        if (!isAnimatingRef.current) {
          break;
        }

        const lineId = lineIds[index];
        currentLineIndexRef.current = index;
        const data = winningData[lineId];

        if (!data) {
          continue;
        }

        // One tick per line as the cycle steps onto it. Scatter carries a null
        // lineId and is skipped — it is announced by its own sound, not by this.
        //
        // NOT gated on WIN_LINES.ENABLED, which is what it used to be and is
        // why this never made a sound: that flag turns on the drawn LINE
        // graphics, and this game draws win BOXES instead, so it is false here.
        // The cycle runs either way, and the sound belongs to the cycle.
        if (
          SOUND_NAME.win_line &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          soundManager.play(SOUND_NAME.win_line);
        }

        // Find a target reel for positioning (prefer middle, then second reel)
        let targetReelIndex = null;
        // ...and the line's row there: the line amount sits on that cell
        // (2026-10-13 — "per line it goes in the middle reel, up and down with
        // the lines"). Null -> it stays centred (a scatter).
        let targetRow = null;

        for (let i = 0; i < data.positions.length; i++) {
          const [reelIndex, row] = data.positions[i];
          if (reelIndex === GAME_LAYOUT.MIDDLE_REEL) {
            targetReelIndex = GAME_LAYOUT.MIDDLE_REEL;
            targetRow = row;
            break;
          }
        }

        if (targetReelIndex === null) {
          for (let i = 0; i < data.positions.length; i++) {
            const [reelIndex, row] = data.positions[i];
            if (reelIndex === GAME_LAYOUT.SECOND_REEL) {
              targetReelIndex = GAME_LAYOUT.SECOND_REEL;
              targetRow = row;
              break;
            }
          }
        }

        // Sync win animations and hide textures for current winning positions
        // Pre-compute line display window for per-symbol loop calculation
        const lineLoopsConfig = winLoops(
          useFastWinRef.current,
          FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
        );
        const lineMasterMs = SpineAnimationData.getMasterWinDuration();
        const lineFastMs = SpineAnimationData.getFastWinDuration();
        const lineAnimType = useFastWinRef.current
          ? (FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST?.ANIMATION_NAME || "win_fast")
          : "win";
        const lineDisplayWindowMs = ((useFastWinRef.current && lineFastMs !== null) ? lineFastMs : lineMasterMs) * lineLoopsConfig;

        // ── Retire the previous text with its `out`, not a hard kill ──
        //
        // hide() is a HARD kill: alpha 0.001 on the spine, the bitmap and the
        // container, plus clearTracks(). Nothing fades — the text snaps out of
        // existence. That is why the `out` animation was never seen: every one
        // of the win controller's call sites used hide(), and playOut() — which
        // WinText.jsx exposes on the ref for exactly this — was dead code.
        //
        // playOut() sets `out` on the track and resolves on its own `complete`,
        // then _finalizeOut() does the same teardown hide() would have. Not
        // awaited on purpose: the outgoing text fades while the incoming one
        // plays its `in`, which is a crossfade rather than a gap.
        //
        // hide() is still right where a spin CANCELS a win — resetAllEffects and
        // snapClearAll — because there the text must be gone on the same frame.
        if (lineWinTextRef?.current) {
          lineWinTextRef.current.playOut();
        }
        if (globalLineWinTextRef?.current) {
          globalLineWinTextRef.current.playOut();
        }

        // Show win amount for this line (skip zero-win entries like synthetic coin triggers)
        if (data.win && lineWinTextRef?.current) {
          // ── Fit in -> idle -> out into THIS line's window ──
          //
          // Always, not just on a fast win. targetDuration only ever speeds the
          // chain UP (see _getAnimationChainDuration), so a chain shorter than
          // the line still plays at native speed and finishes its own `out`.
          // A chain LONGER than the line is compressed to fit, which is what
          // was missing: the next line opens by hard-killing this text with
          // hide(), so an overlong chain never reached `out` at all — the text
          // simply vanished.
          //
          // It used to be gated on WIN_FAST.WIN_TEXT_SPEED_UP, so disabling
          // WIN_FAST left the chain permanently unscaled.
          // On the line's middle-reel cell (native units, origin = grid
          // centre); a line without one (a scatter) stays centred.
          lineWinTextRef.current.moveTo?.(
            data.lineId != null && targetRow != null
              ? (targetRow - (GAME_LAYOUT.SYMBOLS_VISIBLE - 1) / 2 + WIN_TEXT_CONFIG.LINE_WIN_Y_IN_CELL) *
                  Settings.SYMBOL_HEIGHT
              : 0,
          );
          lineWinTextRef.current.showWinAmount(data.win, {
            targetDuration: lineDisplayWindowMs,
          });
        }

        const currentLinePositions = data.positions.map(
          ([reelIndex, symbolIndex]) => {
            const reelController =
              reelsetControllerRef.current.reelControllers[reelIndex];
            const symbol =
              reelController?.symbolsContainer?.children?.[symbolIndex];
            const symbolId = symbol?.currentSymbolId || symbol?.symbolId;
            return [reelIndex, symbolIndex, symbolId];
          },
        );

        // Pre-compute winning position Set for O(1) lookups
        const winPosSet = new Set(
          currentLinePositions.map(([r, s]) => `${r}_${s}`),
        );
        const reelsInCurrentLine = new Set(
          currentLinePositions.map(([reelIndex]) => reelIndex),
        );

        reelsetControllerRef.current.executeOnOverlayReelControllers(
          (overlayReelController) => {
            if (!overlayReelController?.isExpandingWildVisible) {
              overlayReelController?.hideAllAnimationsExcept(
                currentLinePositions,
              );
            }
          },
        );

        if (
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          expandingWildReelIndicesRef.current.length > 0
        ) {
          const reelsArray = Array.from(reelsInCurrentLine).sort(
            (a, b) => a - b,
          );
          let rightmostConnected = -1;
          for (let i = 0; i < reelsArray.length; i++) {
            const currentReel = reelsArray[i];

            if (i === 0 && currentReel > 1) {
              break;
            }

            if (i > 0 && currentReel !== reelsArray[i - 1] + 1) {
              break;
            }

            rightmostConnected = currentReel;
          }

          expandingWildReelIndicesRef.current.forEach((wildReelIndex) => {
            const overlayReelRef = overlayReelRefs.current?.[wildReelIndex];
            const wildReelHasWinningPosition =
              reelsInCurrentLine.has(wildReelIndex);
            const shouldBeBright =
              wildReelHasWinningPosition && rightmostConnected >= wildReelIndex;
            const currentlyBright =
              expandingWildAnimationStateRef.current[wildReelIndex] ?? true;

            if (shouldBeBright && !currentlyBright) {
              overlayReelRef?.playExpandingWildWinAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = true;
            } else if (!shouldBeBright && currentlyBright) {
              overlayReelRef?.playExpandingWildDarkAnimation?.();
              expandingWildAnimationStateRef.current[wildReelIndex] = false;
            }
          });
        }

        // if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        //   winLinesContainerRef.current.hideWinLine();
        // }
        // No clear per line (2026-10-13) — showWinLine below keeps a line
        // that is already burning and switches only to a different one, like
        // the boxes. A step with no line hides it there.

        // if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        //   winBoxContainerRef.current.hideWinBoxes();
        // }
        // No clear per line any more — showWinBoxes below diffs against the
        // boxes already up (a shared cell's box keeps playing), as the symbols
        // do (2026-10-12). A line with no path clears them there instead.

        // Diff-based texture transitions — only mutate symbols that changed between lines
        const prevSet = prevWinPosSetRef.current;
        const reelIndices = GAME_LAYOUT.REEL_INDICES;

        if (prevSet) {
          // Show textures for positions that WERE winning but no longer are
          for (const prevKey of prevSet) {
            if (!winPosSet.has(prevKey)) {
              const [reel, symbolIndex] = prevKey.split("_").map(Number);
              const reelController =
                reelsetControllerRef.current.reelControllers[reel];
              if (reelController?.showSymbolTexture) {
                reelController.showSymbolTexture(symbolIndex);
              }
            }
          }
        } else {
          // First line — show all non-winning textures (full reset)
          for (let ri = 0; ri < reelIndices.length; ri++) {
            const reel = reelIndices[ri];
            const reelController =
              reelsetControllerRef.current.reelControllers[reel];
            if (!reelController) continue;

            for (
              let symbolIndex = 0;
              symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
              symbolIndex++
            ) {
              if (!winPosSet.has(`${reel}_${symbolIndex}`)) {
                if (reelController.showSymbolTexture) {
                  reelController.showSymbolTexture(symbolIndex);
                }
              }
            }
          }
        }

        // ── The bonus trigger holds its celebration back ──
        //
        // A line carrying a reveal symbol plays NO win animation while the
        // reveal runs. The board just sits under the dim overlay, the scatter
        // opens and spins its strip, and only when the strip lands do all
        // three scatters celebrate together — see the revealPromise block
        // below. Firing the win here as well made two symbols celebrate for
        // several seconds while the third was still deciding what it was.
        //
        // Only while the reveal is still to come. Once it has finished, later
        // passes of the cycle animate normally — otherwise the line would go
        // silent for the rest of the display.
        // Released only once the reveal has finished AND a package was really
        // awarded. A BLANK keeps this true for the rest of the display, and
        // that is what suppresses its win animations completely — it awarded
        // nothing, so there is nothing to celebrate.
        const revealSettledAndPaid =
          revealDoneForRoundRef.current ===
            (currentRoundRef.current?.id ?? null) &&
          revealAwardedForRoundRef.current ===
            (currentRoundRef.current?.id ?? null);
        const lineHasReveal =
          !revealSettledAndPaid &&
          (data.positions || []).some(([rIdx, sIdx]) => {
            const ctrl =
              reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
            const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
            return ctrl?.hasRevealSequence?.(sid);
          });

        const positions = data.positions;
        for (let i = 0; i < positions.length; i++) {
          const [reelIndex, symbolIndex] = positions[i];
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) continue;

          const hasExpandingWildFullReel =
            FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
            FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
            overlayReelController.isExpandingWildVisible === true;

          if (hasExpandingWildFullReel) {
            continue;
          }

          // Reveal line: no win yet. The reveal cell still hides its reel
          // texture, because its overlay spine is about to take the cell over.
          // The others keep their texture — hiding it with no animation to
          // replace it is what left them blank.
          if (lineHasReveal) {
            const sid = currentRoundRef.current?.reels?.[reelIndex]?.[symbolIndex];
            if (overlayReelController.hasRevealSequence?.(sid)) {
              reelController.hideSymbolTexture?.(symbolIndex);
            }
            continue;
          }

          // CRITICAL: Read from spinningSprites (which has wild replacements)
          const VISIBLE_SYMBOL_START_INDEX = 1 + GAME_LAYOUT.SYMBOLS_VISIBLE;
          const spriteIndex = VISIBLE_SYMBOL_START_INDEX + symbolIndex;
          const sprite = reelController.spinningSprites?.[spriteIndex];

          if (sprite) {
            const actualSymbolId = sprite.currentSymbolId;
            if (actualSymbolId !== undefined) {
              // Per-symbol loop count: how many times this symbol's animation fits in the line display window
              const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWinRef.current);
              const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && lineDisplayWindowMs > 0)
                ? Math.max(1, Math.round(lineDisplayWindowMs / symbolWinMs))
                : 1;

              // Feature trigger lines (scatter/coin) loop INDEFINITELY until
              // the feature takes the screen — finite loops cause
              // play→stop→restart jitter, and leave the board dead while the
              // player is still deciding on the gamble screen.
              // Cleopatra's Fire retrigger: its scatters are a trigger line
              // too — they keep playing under the popup until the next spin
              // clears them, exactly as the intro's do.
              const isFeatureTriggerLine =
                (data.isScatter && store.getState().freeSpins.triggered) ||
                (data.isScatter &&
                  store.getState().freeSpins.currentRound?.freeSpinsRetrigger > 0) ||
                // (data.isCoin && (
                //   store.getState().featureGame.royalCollectTriggered ||
                //   store.getState().potCollection.potCollectionTriggered
                // ));
                (data.isCoin && (
                  store.getState().featureGame.royalCollectTriggered ||
                  store.getState().potCollection.potCollectionTriggered ||
                  selectHoldAndWinIntroPending(store.getState())
                ));
              const effectiveLoops = isFeatureTriggerLine ? 0 : perSymbolLoops;

              // overlayReelController.syncWinAnimation(
              //   symbolIndex,
              //   actualSymbolId,
              //   lineAnimType,
              //   null,
              //   effectiveLoops,
              // );
              // ── The Hold & Win trigger coins WAIT, they do not win ──
              // (2026-10-07) their `win` is saved for the scarab; every one
              // plays the anticipation wait (Games/Shared/triggerCoinWait).
              if (data.isCoin && selectHoldAndWinIntroPending(store.getState())) {
                playTriggerCoinWait(overlayReelController, symbolIndex, actualSymbolId, lineAnimType, null, store.getState().freeSpins.currentRound);
              } else {
                overlayReelController.syncWinAnimation(
                  symbolIndex,
                  actualSymbolId,
                  lineAnimType,
                  null,
                  effectiveLoops,
                );
              }
            }
          }

          if (reelController.hideSymbolTexture) {
            reelController.hideSymbolTexture(symbolIndex);
          }
        }

        // Store current positions for next line's diff
        prevWinPosSetRef.current = winPosSet;

        if (
          FEATURES_CONFIG.WIN_LINES.ENABLED &&
          winLinesContainerRef.current &&
          data.lineId !== undefined &&
          data.lineId !== null
        ) {
          // winLinesContainerRef.current.showWinLine(data.lineId);
          // No line inside this line's win boxes (2026-10-13).
          winLinesContainerRef.current.showWinLine(
            data.lineId,
            boxedReelsOnLine(data.line, data.positions),
          );
        } else if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          // A step with no payline (a scatter): the last line goes.
          winLinesContainerRef.current.hideWinLine();
        }

        // Show win boxes — either on winning positions only or full line path
        if (
          FEATURES_CONFIG.WIN_BOX.ENABLED &&
          winBoxContainerRef.current &&
          data.line
        ) {
          const winBoxPositions = FEATURES_CONFIG.WIN_BOX.WINNING_ONLY
            ? data.positions // Only matched/winning symbol positions
            : data.line.map((row, reelIndex) => [reelIndex, row]); // Full line path

          // Compute target loops from pre-cached durations
          let winBoxTargetLoops = 0;
          // The line's window — the box plays its loops in exactly this.
          let winBoxWindowMs = 0;
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          if (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0) {
            const fastWinDur = SpineAnimationData.getFastWinDuration();
            const baseDur = (useFastWinRef.current && fastWinDur !== null)
              ? fastWinDur
              : SpineAnimationData.getMasterWinDuration();
            const lineLoops = winLoops(
              useFastWinRef.current,
              FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
            );
            // winBoxTargetLoops = Math.max(1, Math.floor((baseDur * lineLoops) / winBoxAnimMs));
            // Rounded: 2000 / 666.7 = 2.9998 is 3 loops, filling the line.
            winBoxTargetLoops = Math.max(1, Math.round((baseDur * lineLoops) / winBoxAnimMs));
            winBoxWindowMs = baseDur * lineLoops;
          }

          // winBoxContainerRef.current.showWinBoxes(winBoxPositions, winBoxTargetLoops);
          winBoxContainerRef.current.showWinBoxes(winBoxPositions, winBoxTargetLoops, winBoxWindowMs);
        } else if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          // No line path to box: the last line's boxes go (the old per-line clear).
          winBoxContainerRef.current.hideWinBoxes();
        }

        // ── REVEAL SYMBOLS: started WITH the win, not after it ──
        //
        // Launched with the line, not after it. Waiting for the other symbols
        // to finish their win before opening this one put a dead beat in the
        // middle of the trigger. It is awaited after the line, so the round is
        // still held until the sequence settles — it just no longer starts
        // late.
        //
        // Keyed off the SYMBOL having a reveal sequence, not off the win type.
        // This is a bonus trigger; whether the win that carried it was a
        // scatter, a coin or a payline is irrelevant.
        //
        // ONCE PER ROUND. This sits inside the win-line cycling loop, which
        // repeats for as long as the win display runs.
        // Assigned by the free-spins block below, and called either on the
        // line's own timer or — when a reveal is running — only after the
        // reveal and its celebration have finished. Declared out here so both
        // paths can reach it.
        let openFeatureScreen = null;

        let revealPromise = null;
        const revealRoundId = currentRoundRef.current?.id ?? null;
        if (revealPlayedForRoundRef.current !== revealRoundId) {
          const revealTargets = [];
          for (const [rIdx, sIdx] of data.positions || []) {
            const ctrl =
              reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
            const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
            if (ctrl?.hasRevealSequence?.(sid)) {
              revealTargets.push({ ctrl, sIdx, sid, rIdx });
            }
          }
          if (revealTargets.length > 0) {
            // Claim the round only when the doors ACTUALLY open — mirrors
            // useWinController. Claimed at the top of the block, a pass that
            // found no targets took the round id and every later pass returned
            // at the guard above, leaving the scatters on a dimmed board with
            // the doors shut.
            revealPlayedForRoundRef.current = revealRoundId;
            dispatchRef.current(setCriticalAnimationPlaying(true));
            revealPromise = Promise.all(
              revealTargets.map(({ ctrl, sIdx, sid, rIdx }) => {
                const gambleScatter = currentRoundRef.current?.gambleScatter;
                return ctrl
                  .playRevealSequence(sIdx, sid, gambleScatter)
                  .then(() => {
                    // ── Keep the texture UNDER the overlay honest ──
                    //
                    // The reveal only changed what the overlay shows. The reel
                    // still holds the symbol that landed, so resetAllEffects —
                    // which hides the overlay and then calls
                    // showAllSymbolTextures — resurfaces it on the next spin.
                    // For a blank that means a closed gold door where the
                    // player last saw an empty window.
                    //
                    // TEXTURE ONLY — never the cell's identity. The wild's
                    // replaceSymbolAtPosition would also rewrite
                    // currentSymbolId, and the win cycle reads that field to
                    // pick the celebrating spine; pointing it at a
                    // texture-only symbol stopped the scatter animating and
                    // desynced it from the other two. This cell is still the
                    // gamble scatter, so all three keep celebrating together
                    // on `scg` while only the hidden picture becomes blank.
                    const cell = ctrl.getRevealStaticCell?.(
                      sid,
                      gambleScatter,
                    );
                    if (!cell) return null;
                    reelsetControllerRef.current?.reelControllers?.[
                      rIdx
                    ]?.setSymbolTextureOnly?.(
                      sIdx,
                      cell.symbolId,
                      cell.texture,
                    );
                    // Handed back so the caller can see what was landed —
                    // this is the award signal, straight off the round.
                    return cell;
                  });
              }),
            );
          }
        }

        // Hoisted: the reveal block below opens the feature screen on this
        // same duration, and it is assigned inside the promise.
        let lineDuration = 0;

        await new Promise((resolve) => {
          // Duration from SpineAnimationData (extracted once at init)
          const targetLoops = winLoops(
            useFastWinRef.current,
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.LOOPS_PER_LINE,
          );
          const masterDuration = SpineAnimationData.getMasterWinDuration();
          const fastWinDuration = SpineAnimationData.getFastWinDuration();
          const baseDuration = (useFastWinRef.current && fastWinDuration !== null)
            ? fastWinDuration
            : masterDuration;
          lineDuration = baseDuration * targetLoops;

          // Snap to complete win box loops to prevent partial-loop jitter
          // ── OFF (Vladimir, 2026-09-30: "one line is cut") ── it floored the
          // line to whole box loops: 2000 / 666.7 = 2.9998 -> 2 -> 1333 ms, so
          // the line switched mid-way through the symbols' win. The line follows
          // the symbols' animation; the box's loop count fits it (showWinBoxes).
          // const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          // if (
            // FEATURES_CONFIG.WIN_BOX.ENABLED &&
            // FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP &&
            // winBoxAnimMs > 0
          // ) {
            // const completeLoops = Math.floor(lineDuration / winBoxAnimMs);
            // if (completeLoops >= 1) {
              // lineDuration = completeLoops * winBoxAnimMs;
            // }
          // }

          // Play win_line a second time at half the line duration
          // ── OFF (2026-10-20, Vladimir: "play once, not twice per win
          // line") ── WIN_LINES.ENABLED is on for our fire lines, which woke
          // this second play. One sound per line, on the cycle's step.
          if (
            false &&
            FEATURES_CONFIG.WIN_LINES.ENABLED &&
            SOUND_NAME.win_line &&
            data.lineId !== undefined &&
            data.lineId !== null
          ) {
            const secondPlayTimeout = new TickerTimeout(() => {
              soundManager.play(SOUND_NAME.win_line);
            }, lineDuration / 2);
            currentTimeoutsRef.current.push(secondPlayTimeout);
          }

          // ── FREE SPIN TRIGGER: the gamble screen, not the intro ──
          //
          // GDD §9: the player gambles the PACKAGE before playing it, so the
          // gamble screen comes first and the intro follows once the package
          // is settled. The one exception is a tier-5 (gold) award, which the
          // GDD sends straight to free spins with no gamble offered — the
          // ladder has nothing above it to win.
          //
          // Everything below this point — the wait-for-animated-win poll and
          // the delay — is Zeus's, unchanged. Only the popup differs.
          if (
            FEATURES_CONFIG.FREE_SPINS.ENABLED &&
            data.isScatter &&
            store.getState().freeSpins.triggered
          ) {
            // The gamble screen opens ONLY while there is a question to ask:
            // the ladder unanswered (`gambleSettled` false) AND outcomes left
            // to win (`variants` non-empty — the player who pressed SKIP has
            // answered, and the server marks it by emptying the pool).
            // Otherwise the win replay hands straight to the intro: forcing
            // the gamble screen on a settled package showed a screen with no
            // data to offer. Decided at CALL time, off the slice — the one
            // place both live play and every reload keep current.
            openFeatureScreen = () => {
              const fs = store.getState().freeSpins;
              const canGamble =
                !fs.gambleSettled && (fs.gamble?.variants?.length ?? 0) > 0;
              dispatchRef.current(
                canGamble ? setShowGambleDialog(true) : setShowFreeSpinIntro(true),
              );
            };

            // ── When a reveal is running, DO NOT schedule on the line timer ──
            //
            // The line lasts about as long as one win animation; the reveal
            // takes far longer — doors 0.5s, strip 2.6s, then the stop. On the
            // line timer the gamble screen opened on top of a strip that was
            // still spinning. The revealPromise block below opens it instead,
            // once the strip has landed AND the three win loops have played.
            //
            // Nothing is scheduled here when a reveal is running. Everything
            // below is Zeus's original timing, and it only applies to a
            // trigger with no reveal to wait for.
            if (!revealPromise) {
              // If animated win is still showing (4-5 scatters), wait for it
              // to close first.
              const animState = store.getState();
              if (selectWinAnimationsPlaying(animState)) {
                const waitForAnimatedWin = new Promise((resolve) => {
                  const poll = () => {
                    const currentState = store.getState();
                    if (!selectWinAnimationsPlaying(currentState)) {
                      resolve();
                    } else {
                      const t = new TickerTimeout(poll, 100);
                      currentTimeoutsRef.current.push(t);
                    }
                  };
                  poll();
                });
                waitForAnimatedWin.then(openFeatureScreen);
              } else {
                const delayTimeout = new TickerTimeout(() => {
                  const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                  if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                  openFeatureScreen();
                }, lineDuration / 1.25);
                currentTimeoutsRef.current.push(delayTimeout);
              }
            }
          }

          // ── Cleopatra's Fire retrigger: the intro's timing, verbatim ──
          //
          // The Additional Free Spins popup opens exactly when the intro would:
          // after a big win has closed, or `lineDuration / 1.25` into the
          // scatter line. retriggerWinShown is what lets it open (see the
          // retrigger branch of the charge effect).
          if (
            data.isScatter &&
            !store.getState().freeSpins.triggered &&
            store.getState().freeSpins.currentRound?.freeSpinsRetrigger > 0
          ) {
            const openRetriggerPopup = () => setRetriggerWinShown(true);
            if (selectWinAnimationsPlaying(store.getState())) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  if (!selectWinAnimationsPlaying(store.getState())) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(openRetriggerPopup);
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                openRetriggerPopup();
              }, lineDuration / 1.25);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // ROYAL COLLECT TRIGGER: Show intro after trigger symbol animation finishes
          if (
            FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
            data.isCoin &&
            store.getState().featureGame.royalCollectTriggered
          ) {
            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {
                dispatchRef.current(setShowRoyalCollectIntro(true));
              });
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                dispatchRef.current(setShowRoyalCollectIntro(true));
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // POT COLLECTION TRIGGER: Show intro after trigger symbol animation finishes
          // Skip for mid-bonus reload — we go straight to bonus scene
          if (
            FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
            data.isCoin &&
            store.getState().potCollection.potCollectionTriggered &&
            store.getState().potCollection.reloadPhase !== "respins"
          ) {

            // Dispatch PC intro flag immediately. PotCollectionIntroDialog gates
            // its startSequence on !infoPopupActive so if FR info popup is still
            // open, the intro waits for it to close naturally via React re-render.
            const showPCIntroWhenReady = () => {

              dispatchRef.current(setShowPotCollectionIntro(true));
            };

            const animState = store.getState();
            if (selectWinAnimationsPlaying(animState)) {

              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  const currentState = store.getState();
                  if (!selectWinAnimationsPlaying(currentState)) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(() => {

                showPCIntroWhenReady();
              });
            } else {

              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                showPCIntroWhenReady();
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          // HOLD & WIN TRIGGER: the intro after the trigger coins' line — the
          // base game's opener (useWinController), unchanged. Not on a
          // mid-feature reload: the room is already on screen.
          if (
            data.isCoin &&
            selectHoldAndWinIntroPending(store.getState()) &&
            store.getState().holdAndWin.reloadPhase !== "in_progress"
          ) {
            const showHWIntro = () => {
              dispatchRef.current(setShowHoldAndWinIntro(true));
            };

            if (selectWinAnimationsPlaying(store.getState())) {
              const waitForAnimatedWin = new Promise((resolve) => {
                const poll = () => {
                  if (!selectWinAnimationsPlaying(store.getState())) {
                    resolve();
                  } else {
                    const t = new TickerTimeout(poll, 100);
                    currentTimeoutsRef.current.push(t);
                  }
                };
                poll();
              });
              waitForAnimatedWin.then(showHWIntro);
            } else {
              const delayTimeout = new TickerTimeout(() => {
                const idx = currentTimeoutsRef.current.indexOf(delayTimeout);
                if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                showHWIntro();
              }, lineDuration);
              currentTimeoutsRef.current.push(delayTimeout);
            }
          }

          const timeoutId = new TickerTimeout(() => {
            const idx = currentTimeoutsRef.current.indexOf(timeoutId);
            if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
            resolve();
          }, lineDuration);
          currentTimeoutsRef.current.push(timeoutId);
        });

        // The reveal was launched alongside the win above. Hold the round
        // here until it settles — the scatters' win has already finished by
        // now, the reveal is simply longer.
        if (revealPromise) {
          try {
            const revealCells = await revealPromise;
            revealDoneForRoundRef.current = currentRoundRef.current?.id ?? null;


            // ── Only a WON package earns a presentation ──
            //
            // Up to this point the board has stayed clear: three scatters
            // landed and bounced, the UI was locked, and the strip spun with
            // no dim and no win animations. What happens next depends entirely
            // on what it landed on.
            //
            // Read from the ROUND, via the reveal config's own `awarded`. The
            // outcome has been on the round as `gambleScatter` since the spin
            // resolved, so there is no reason to ask the slice — and asking it
            // meant this path could only ever run on a live server round.
            //
            // A blank falls straight through: nothing celebrates, the screen
            // never dims, and the `finally` below hands the UI back.
            const revealAwarded = (revealCells || []).some((c) => c?.awarded);

            // ── The blank's own sound, the counterpart to scatter_win ──
            //
            // The third scatter is the gold one, and the strip inside it has
            // just stopped. If it stopped on a gamble level the round wins its
            // free spins and `scatter_win` fires below. If it stopped on a
            // BLANK nothing else happens at all — the screen never dims, no
            // line celebrates, and the `finally` hands the UI back. That
            // silence was the whole outcome being delivered without a sound.
            //
            // Fired here rather than in an `else` so the two sit on the same
            // decision, one line apart, and neither can drift from it.
            //
            // Levelled ONTO scatter_win, not near it: both are the same beat
            // answered two ways, so a player must hear which answer came up,
            // not which was louder. See SOUND_VOLUMES.
            if (!revealAwarded) {
              soundManager.play(SOUND_NAME.scatter_lose);
            }
            if (revealAwarded) {
              // Recorded so the earlier suppression gate can release: without
              // it, later passes of the win cycle would keep the trigger line
              // silent for a round that DID pay.
              revealAwardedForRoundRef.current =
                currentRoundRef.current?.id ?? null;
              // Dim NOW — not when the scatters landed. This is the moment
              // there is finally a result worth presenting.
              dimReelSetRef.current?.dimReels(true);

              // ── The trigger's own sound ── mirrors useWinController.
              //
              // The doors are open, the package landed and PAID. Not gated on
              // `hadDeferredWinSound`: that flag only says a basic chime was
              // owed and is false on a big/mega round, while the trigger is
              // the same event whatever the round paid. The flag is still read
              // and cleared above so a stale one cannot leak into the next
              // round.
              soundManager.play(SOUND_NAME.scatter_win);

              // ── NOW all three celebrate ──
              //
              // The strip has landed and the player can see what was won, so
              // the whole trigger line fires at once. 0 = loop for ever, which
              // is what a trigger line has always done — it runs until the
              // feature takes the screen.
              for (const [rIdx, sIdx] of data.positions || []) {
                const ctrl =
                  reelsetControllerRef.current?.overlayReelControllers?.[rIdx];
                const rc =
                  reelsetControllerRef.current?.reelControllers?.[rIdx];
                const sid = currentRoundRef.current?.reels?.[rIdx]?.[sIdx];
                if (!ctrl || sid === undefined) continue;

                if (ctrl.hasRevealSequence?.(sid)) {
                  // The scatter that opened. Its card is a slot object and
                  // survives the animation change, so it keeps showing.
                  ctrl.playRevealWin?.(sIdx);
                } else {
                  rc?.hideSymbolTexture?.(sIdx);
                  ctrl.syncWinAnimation(sIdx, sid, "win", null, 0);
                }
              }

              // ── Then the feature screen, on the ORIGINAL delay ──
              //
              // `lineDuration / 1.25` is the wait this trigger has always
              // used; the only thing that changed is WHERE it is measured
              // from. It could not stay on the line timer because the reveal
              // outlasts the line, so the screen opened over a strip still
              // spinning.
              if (openFeatureScreen) {
                const openTimeout = new TickerTimeout(() => {
                  const idx = currentTimeoutsRef.current.indexOf(openTimeout);
                  if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
                  openFeatureScreen();
                }, lineDuration / 1.25);
                currentTimeoutsRef.current.push(openTimeout);
              }
            }
          } finally {
            // ── Who owns the lock ──
            //
            // A bonus trigger holds criticalAnimationPlaying from the moment
            // the scatters land until the round actually starts, so nothing
            // underneath can be spun while the reveal, the gamble screen and
            // the intro are on top of it. Releasing it here — the instant the
            // reveal settled — handed the UI back with the gamble screen still
            // open.
            //
            // It is released in exactly two places, both of which end the
            // trigger: the wipe's midpoint when the round begins, and the
            // gamble dialog's close-after-loss when the package is forfeit.
            if (!store.getState().freeSpins.triggered) {
              dispatchRef.current(setCriticalAnimationPlaying(false));
            }
          }
        }

        // After the first line's animation completes, fire the callback to unblock spin
        // Past every skip, so this line was really presented.
        playedAny = true;

        if (index === 0 && onFirstLineComplete) {
          onFirstLineComplete();
          onFirstLineComplete = null; // Only fire once
        }


      }

      return playedAny;
    },
    [
      winLinesContainerRef,
      winBoxContainerRef,
      globalLineWinTextRef,
      overlayReelRefs,
      lineWinTextRef,
      // Added with the reveal's own dim — this callback now owns the dim for a
      // trigger, which it previously never touched.
      dimReelSetRef,
    ],
  );

  /**
   * Shows all winning symbols simultaneously before line cycling begins.
   * Creates a celebration effect by displaying all winning animations at once.
   */
  const showAllWinningSymbolsOnce = useCallback(
    async (
      winningData,
      hasExpandingWilds,
      expandingWildReelIndices = [],
      winType = null,
      isAnimatedWin = false,
    ) => {
      const allPositions = [];
      const lineIds = Object.keys(winningData);

      // ── A TRIGGER ENTRY IS NOT A WIN ──
      //
      // Show-all lights every winning cell at once. The trigger's own entry —
      // the synthetic scatter win, or a coin trigger — carries POSITIONS and
      // pays 0, so including it lit the scatters as though they had been paid.
      // On a Golden Mystery round that ends on a BLANK gamble scatter it was at
      // its worst: the empty window celebrated beside the coins that actually
      // paid. Vladimir, 2026-09-18: *"we didnt win that thing, so only top 2
      // coins should be winning."*
      //
      // Nothing is lost. The scatters get their own beat immediately after —
      // orderLineIds sorts the trigger entry LAST and the cycle jumps straight
      // to it — which is where they are meant to light, together, once.
      //
      // A round whose ONLY wins are trigger entries never reaches here
      // (`skipShowAllForTriggerOnly`), but the fallback keeps that honest: if
      // the filter empties the list, show everything rather than nothing.
      // `isScatter` only — NOT `isCoin`.
      //
      // The two are not the same kind of entry. A scatter trigger pays 0 and
      // exists to carry positions. `isCoin` is Pot Collection's and Royal
      // Collect's trigger line and it carries REAL money (RGS.js, the "coin"
      // tuple), so filtering it here would leave paid cells dark on those
      // games. The Golden Mystery's own payout is `isFeaturePayout` and is
      // untouched by either test.
      const payingLineIds = lineIds.filter((lineId) => {
        const data = winningData[lineId];
        return !data?.isScatter;
      });
      const showAllLineIds = payingLineIds.length ? payingLineIds : lineIds;

      showAllLineIds.forEach((lineId) => {
        const data = winningData[lineId];
        data.positions.forEach((position) => {
          const [reelIndex, symbolIndex] = position;
          const exists = allPositions.some(
            ([r, s]) => r === reelIndex && s === symbolIndex,
          );
          if (!exists) {
            allPositions.push(position);
          }
        });
      });

      // Targeted cleanup: hide landing/idle animations on NON-winning positions only.
      // Build a set of winning positions per reel so we can exclude them.
      const winPositionsByReel = new Map();
      allPositions.forEach(([reelIndex, symbolIndex]) => {
        if (!winPositionsByReel.has(reelIndex)) {
          winPositionsByReel.set(reelIndex, new Set());
        }
        winPositionsByReel.get(reelIndex).add(symbolIndex);
      });

      executeOnOverlayReelControllersRef.current(
        (overlayReelController, reelIndex) => {
          if (overlayReelController) {
            const exceptPositions = winPositionsByReel.get(reelIndex) || null;
            overlayReelController.hideAllAnimations(exceptPositions);
          }
        },
      );

      executeOnReelControllersRef.current((controller) => {
        if (controller && controller.showAllSymbolTextures) {
          controller.showAllSymbolTextures();
        }
      });

      // ── Play win sound for non-animated wins ──
      //
      // The regular symbols' win, played HERE and on its own — this runs before
      // the doors open. No longer withheld: the doors have their own sound, and
      // the reveal CUTS this chime as it opens (`cutSound`) rather than this
      // site trying to predict the reveal and stay silent for it.
      // Same swap as the other site — see goldenWinSound. These two are
      // mutually exclusive, so gating just one of them is never enough.
      if (!isAnimatedWin) {
        const winChime = goldenWinSound(winningData);
        if (winChime) soundManager.play(winChime);
      }

      // === STEP 3: Determine win animation type (fast vs regular) ===
      const winFastConfig = FEATURES_CONFIG.WIN_ANIMATION_TIMING.WIN_FAST;
      // Speed, not timing — the player's setting, as the base game reads it.
      const isAutoplayActive = store.getState().settings.autoPlayActive;
      const useFastWin =
        winFastConfig?.ENABLED &&
        (spacebarTurboActiveRef.current || isAutoplayActive);
      const winAnimationType = useFastWin
        ? (winFastConfig.ANIMATION_NAME || "win_fast")
        : "win";

      // Persist fast-win decision so cycleThroughLines uses the same timing/animation
      useFastWinRef.current = useFastWin;

      // Compute display window duration for "show all wins" phase.
      //
      // One definition, shared — see SpineAnimationData.getShowAllWindowMs. The
      // outro waits on the same beat, and two copies of this arithmetic would
      // drift the moment either loop count changed.
      const showAllTurbo = isTurboLike(store.getState().settings.autoPlaySpeed);
      const displayWindowMs = SpineAnimationData.getShowAllWindowMs({
        useFastWin,
        turbo: showAllTurbo,
      });
      // Still needed on its own further down, where the expanding-wild show-all
      // substitutes its own loop count for this one.
      const showAllNormalLoops = showAllTurbo
        ? FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL_TURBO
        : FEATURES_CONFIG.SHOW_ALL_WINS.LOOPS_FOR_SHOW_ALL;

      // Start spine win animations on winning symbols
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];

        if (!overlayReelController || !reelController) {
          continue;
        }

        const hasExpandingWildFullReel =
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        const symbol = reelController.symbolsContainer.children[symbolIndex];
        if (symbol) {
          const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;
          if (actualSymbolId !== undefined) {
            // Per-symbol loop count: how many times this symbol's animation fits in the display window
            const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
            const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
              ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
              : 1;
            // overlayReelController.showWinAnimation(symbolIndex, actualSymbolId, winAnimationType, null, perSymbolLoops);
            // A Hold & Win TRIGGER coin waits, it does not win (2026-10-07,
            // Games/Shared/triggerCoinWait) — the base game's twin.
            if (isCoinSymbol(actualSymbolId) && selectHoldAndWinIntroPending(store.getState())) {
              playTriggerCoinWait(overlayReelController, symbolIndex, actualSymbolId, winAnimationType, null, store.getState().freeSpins.currentRound);
            } else {
              overlayReelController.showWinAnimation(symbolIndex, actualSymbolId, winAnimationType, null, perSymbolLoops);
            }

            // The base game's twin — see the same block in useWinController.
            // A Golden cell's win moves the whole cell, so the reel's flat
            // composite is left standing behind it and the coin is drawn twice.
            // Only Golden cells; showAllSymbolTextures puts it back.
            if (overlayReelController.hasGoldenMystery?.(symbolIndex)) {
              reelController.hideSymbolTexture?.(symbolIndex);
            }
          }
        }
      }

      const isFullReelMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel";
      const isCascadeMode =
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "cascade";

      if (!isFullReelMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];
          if (reelController && reelController.showAllSymbolTextures) {
            reelController.showAllSymbolTextures();
          }
        });
      }

      // In cascade mode, transition cascaded wild symbols to "win" animation
      // ONLY if they are part of an actual win line (non-winning wilds stay as static texture)
      if (isCascadeMode && expandingWildReelIndices.length > 0) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelController =
            reelsetControllerRef.current.overlayReelControllers[reelIndex];
          const reelController =
            reelsetControllerRef.current.reelControllers[reelIndex];

          if (!overlayReelController || !reelController) return;

          const winningPositionsOnReel = winPositionsByReel.get(reelIndex);

          for (
            let symbolIndex = 0;
            symbolIndex < GAME_LAYOUT.SYMBOLS_VISIBLE;
            symbolIndex++
          ) {
            // Only animate wilds that are part of a winning line
            if (
              !winningPositionsOnReel ||
              !winningPositionsOnReel.has(symbolIndex)
            ) {
              continue;
            }

            const symbol =
              reelController.symbolsContainer.children[symbolIndex];
            if (symbol) {
              const actualSymbolId = symbol.currentSymbolId || symbol.symbolId;

              if (actualSymbolId === WILD_SYMBOL_ID) {
                const symbolWinMs = SpineAnimationData.getSymbolWinDuration(actualSymbolId, useFastWin);
                const perSymbolLoops = (symbolWinMs && symbolWinMs > 0 && displayWindowMs > 0)
                  ? Math.max(1, Math.round(displayWindowMs / symbolWinMs))
                  : 1;
                overlayReelController.showWinAnimation(
                  symbolIndex,
                  actualSymbolId,
                  winAnimationType,
                  null,
                  perSymbolLoops,
                );
              }
            }
          }
        });
      }

      // === STEP 4: Hide textures behind spines (reveal spine animations) ===
      for (let i = 0; i < allPositions.length; i++) {
        const [reelIndex, symbolIndex] = allPositions[i];
        const reelController =
          reelsetControllerRef.current.reelControllers[reelIndex];
        const overlayReelController =
          reelsetControllerRef.current.overlayReelControllers[reelIndex];
        const hasExpandingWildFullReel =
          overlayReelController &&
          FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
          FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
          overlayReelController.isExpandingWildVisible === true;

        if (hasExpandingWildFullReel) {
          continue;
        }

        // ── A Golden cell's texture IS its picture. Never hide it ──
        //
        // This step clears the flat symbol so the spine started above shows
        // through. A Golden cell has no such spine: the feature owns the cell
        // end to end, and what it leaves behind is a composite on the reel —
        // frame, window and outcome baked into one texture.
        //
        // Hidden, that cell goes blank. It is reachable through the TRIGGER
        // win, whose positions are the scatters wherever they came from, and a
        // scatter the feature dropped is a Golden cell. Nothing was drawn over
        // it, so the row simply emptied.
        if (overlayReelController?.hasGoldenMystery?.(symbolIndex)) {
          continue;
        }

        if (reelController && reelController.hideSymbolTexture) {
          reelController.hideSymbolTexture(symbolIndex);
        }
      }

      // === STEP 5: Show win lines (on top of active spines) ===
      // === STEP 5: Show win lines (on top of active spines) ===
      if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
        const regularLineIds = lineIds
          .map((key) => winningData[key]?.lineId)
          .filter((id) => id !== undefined && id !== null);
        if (regularLineIds.length > 0) {
          // winLinesContainerRef.current.showMultipleWinLines(regularLineIds);
          // No line inside ANY win box (every winning cell is boxed in
          // show-all) — per line, the reels where it crosses one (2026-10-13).
          // The same cells STEP 5b boxes: not coins, scatters, feature payouts.
          const boxedCells = lineIds.flatMap((key) => {
            const d = winningData[key];
            if (!d || d.isCoin || d.isScatter || d.isFeaturePayout) return [];
            return d.positions || [];
          });
          const hiddenReelsByLine = {};
          lineIds.forEach((key) => {
            const d = winningData[key];
            if (d?.lineId == null) return;
            hiddenReelsByLine[d.lineId] = boxedReelsOnLine(d.line, boxedCells);
          });
          // winLinesContainerRef.current.showMultipleWinLines(regularLineIds, hiddenReelsByLine);
          // // A line that repeats a path already shown is left out (2026-10-14).
          // const uniqueLineIds = uniqueWinLineIds(
          //   lineIds
          //     .map((key) => winningData[key])
          //     .filter((d) => d?.lineId != null)
          //     .map((d) => ({
          //       lineId: d.lineId,
          //       rows: d.line,
          //       hiddenReels: hiddenReelsByLine[d.lineId],
          //     })),
          // );
          // winLinesContainerRef.current.showMultipleWinLines(uniqueLineIds, hiddenReelsByLine);
          // EVERY line, each shared stretch drawn once (2026-10-14): the
          // controller needs each line's rows to match the stretches.
          const rowsByLine = {};
          lineIds.forEach((key) => {
            const d = winningData[key];
            if (d?.lineId != null && Array.isArray(d.line)) rowsByLine[d.lineId] = d.line;
          });
          winLinesContainerRef.current.showMultipleWinLines(
            regularLineIds,
            hiddenReelsByLine,
            rowsByLine,
          );
        }
      }

      // === STEP 5b: Show win boxes on all line positions ===
      if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
        const allLinePositions = [];
        lineIds.forEach((key) => {
          const data = winningData[key];
          // Skip trigger entries — no winboxes for synthetic wins.
          //
          // Scatters join coins here. A win box frames a PAYING combination,
          // and a trigger entry pays 0: it exists only so the symbols animate
          // and the feature screen has something to open from. Boxing it tells
          // the player they were paid for the scatters, which never happens.
          if (data?.isCoin || data?.isScatter) return;
          // ── And a feature's own payout is not boxed either ──
          //
          // The base game's rule, verbatim (useWinController) — it was simply
          // never copied here, so the room framed the Golden Mystery's coins
          // and the base game did not.
          //
          // For the opposite reason to the triggers above: not because it pays
          // nothing, but because the cells it pays for ARE the whole
          // presentation. The coins stand on the board with their values
          // printed on them; framing them adds a second, quieter statement of
          // the same thing, and the reference game does not.
          if (data?.isFeaturePayout) return;
          if (FEATURES_CONFIG.WIN_BOX.WINNING_ONLY && data?.positions?.length > 0) {
            // WINNING_ONLY: use only matched symbol positions
            allLinePositions.push(data.positions);
          } else if (data?.line) {
            // Full line path
            allLinePositions.push(
              data.line.map((row, reelIndex) => [reelIndex, row]),
            );
          } else if (data?.positions?.length > 0) {
            // Scatter win (no line) — use actual positions directly
            allLinePositions.push(data.positions);
          }
        });
        if (allLinePositions.length > 0) {
          // Show-all-wins: compute win box loops from display window (same timing as symbols)
          const winBoxAnimMs = SpineAnimationData.getWinBoxDuration();
          const winBoxLoops = (FEATURES_CONFIG.WIN_BOX.SNAP_TO_LOOP && winBoxAnimMs > 0 && displayWindowMs > 0)
            // ? Math.max(1, Math.floor(displayWindowMs / winBoxAnimMs))
            // Rounded, not floored: 2000 / 666.7 = 2.9998 floored to 2 loops
            // and the box froze on its last frame for the last third of the
            // global win (Vladimir, 2026-09-30). Round fills the window.
            ? Math.max(1, Math.round(displayWindowMs / winBoxAnimMs))
            : 0;
          // winBoxContainerRef.current.showAllWinBoxes(allLinePositions, winBoxLoops);
          winBoxContainerRef.current.showAllWinBoxes(allLinePositions, winBoxLoops, displayWindowMs);
        }
      }

      // === STEP 6: Show win text (overlay on top of everything) ===
      if (globalLineWinTextRef?.current && currentRoundRef.current?.win) {
        // const winTextOptions = {};
        // ── Turbo: a FLASH fitted to its 1-loop window (2026-10-08) ── as
        // the base game (useWinController, STEP 6); other speeds unchanged.
        const winTextOptions = showAllTurbo ? { flashMs: displayWindowMs } : {};
        // Speed up WinText to fit in→idle→out within fast win duration
        if (useFastWin && winFastConfig?.WIN_TEXT_SPEED_UP) {
          winTextOptions.targetDuration =
            FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPINE_LOOP_DURATION; // 1000ms (matches win_fast)
        }
        globalLineWinTextRef.current.showWinAmount(currentRoundRef.current.win, winTextOptions);
      }

      // === STEP 7: Dim reels LAST (everything is ready, dim reveals the full composition) ===
      //
      // ...unless a trigger's reveal has not decided yet. Dimming when three
      // scatters land presents a win before the strip has chosen anything, and
      // for a blank it presents one that never comes. The dim moved into the
      // reveal block in cycleThroughLines, which fires it only once the strip
      // has landed AND a package was actually awarded.
      const showAllRound = currentRoundRef.current;
      const showAllRevealPending =
        showAllRound?.triggerReveal === true &&
        revealDoneForRoundRef.current !== (showAllRound?.id ?? null);
      if (dimReelSetRef.current && !showAllRevealPending) {
        dimReelSetRef.current.dimReels(true);
      }

      if (
        FEATURES_CONFIG.EXPANDING_WILDS.ENABLED &&
        FEATURES_CONFIG.EXPANDING_WILDS.ANIMATION_MODE === "full_reel" &&
        expandingWildReelIndices.length > 0
      ) {
        expandingWildReelIndices.forEach((reelIndex) => {
          const overlayReelRef = overlayReelRefs.current?.[reelIndex];
          overlayReelRef?.playExpandingWildWinAnimation?.();
          expandingWildAnimationStateRef.current[reelIndex] = true;
        });
      }

      // Event-based timing: use pre-cached durations from SpineAnimationData
      await new Promise((resolve) => {
        const masterDuration = SpineAnimationData.getMasterWinDuration();
        const fastWinDuration = SpineAnimationData.getFastWinDuration();

        // The GLOBAL show-all hold — how long every winning symbol stays up
        // before line cycling. Routed through winLoops so a fast win gets
        // WIN_FAST.LOOPS here too; this is the duration a show-all round
        // actually displays for, and leaving it at one loop of `win_fast` is
        // what made the win a flash.
        const targetLoops = winLoops(
          useFastWin,
          hasExpandingWilds
            ? FEATURES_CONFIG.EXPANDING_WILDS
                .LOOPS_FOR_EXPANDING_WILD_SHOW_ALL
            : showAllNormalLoops,
        );

        const activeDuration = (useFastWin && fastWinDuration !== null)
          ? fastWinDuration
          : masterDuration;
        const displayDuration = activeDuration * targetLoops;

        // Unblock spin button — timing depends on input mode:
        // Manual mode: unblock after SPIN_UNBLOCK_DELAY (1000ms) so user can hold spacebar to turbo.
        // Autoplay / spacebar turbo: unblock at full displayDuration so animations complete cleanly before next spin.
        let unblockDelay;
        const isAutoplay = BONUS_AUTO_DRIVEN;

        if (spacebarTurboActiveRef.current || isAutoplay) {
          // Both spacebar turbo and autoplay: unblock at full display duration.
          // The continuous effect fires immediately on counterAnimationFinished — no extra SPIN_DELAY needed.
          unblockDelay = displayDuration;
        } else {
          // Manual mode: fixed delay so user can hold spacebar to turbo after 1s
          unblockDelay = FEATURES_CONFIG.WIN_ANIMATION_TIMING.SPIN_UNBLOCK_DELAY;
        }

        const unblockTimeoutId = new TickerTimeout(() => {
          const idx = currentTimeoutsRef.current.indexOf(unblockTimeoutId);
          if (idx > -1) currentTimeoutsRef.current.splice(idx, 1);
          dispatch(setCounterAnimationFinished(true));
        }, unblockDelay);
        currentTimeoutsRef.current.push(unblockTimeoutId);

        // Full display duration — animations keep playing after spin unblocks
        const displayTimeoutId = new TickerTimeout(() => {
          const index = currentTimeoutsRef.current.indexOf(displayTimeoutId);
          if (index > -1) {
            currentTimeoutsRef.current.splice(index, 1);
          }
          resolve();
        }, displayDuration);
        currentTimeoutsRef.current.push(displayTimeoutId);
      });

      // Release the early animation lock so startWinningLinesAnimation can take over
      isAnimatingRef.current = false;

      // Use spacebarTurboActiveRef (synchronous) instead of turboActiveRef (useEffect-batched).
      // turboActiveRef can be stale if user released spacebar but React hasn't re-rendered yet.


      if (spacebarTurboActiveRef.current && !isRevealRound()) {
        return false;
      }

      return true;
    },
    [
      globalLineWinTextRef,
      dimReelSetRef,
      winLinesContainerRef,
      winBoxContainerRef,
      overlayReelRefs,
      isRevealRound,
      dispatch,
      spacebarTurboActiveRef,
    ],
  );



  /**
   * Starts the win line animation cycle.
   * Cycles through each winning line, showing animations and win amounts.
   * For feature rounds, reorders lines to show scatter first.
   */
  const startWinningLinesAnimation = useCallback(
    async (winningData) => {
      if (isAnimatingRef.current) {
        return;
      }

      const lineIds = Object.keys(winningData);
      const winTypeLocal = winType;
      const isAnimatedWinLocal = isAnimatedWin;

      if (winTypeLocal && !winSoundPlayedRef.current) {
        if (isAnimatedWinLocal) {
          winSoundPlayedRef.current = true;
        }
      }

      isAnimatingRef.current = true;
      // Force-complete counter state before clearing timeouts.
      // clearAllTimeouts() kills the pending unblock timeout (line 2093-2097)
      // that would have dispatched counterAnimationFinished(true). Without this,
      // the turbo Continuous Effect stalls waiting for a transition that never comes.
      const animState = store.getState().animations;
      if (animState.counterAnimationStarted && !animState.counterAnimationFinished) {
        dispatch(setCounterAnimationFinished(true));
      }
      clearAllTimeouts();

      if (globalLineWinTextRef?.current) {
        globalLineWinTextRef.current.hide();
      }

      // Clear previous win positions for clean first-line diff
      prevWinPosSetRef.current = null;

      // Held back while a trigger's reveal is still to come — same rule as
      // STEP 7 in showAllWinningSymbolsOnce. The reveal block in
      // cycleThroughLines owns the dim for a trigger.
      const startRound = currentRoundRef.current;
      const startRevealPending =
        startRound?.triggerReveal === true &&
        revealDoneForRoundRef.current !== (startRound?.id ?? null);
      if (dimReelSetRef.current && !startRevealPending) {
        dimReelSetRef.current.dimReels(true);
      }

      winningDataRef.current = winningData;
      lineIdsRef.current = lineIds.slice();
      currentLineIndexRef.current = 0;

      const runSeamlessAnimationCycle = async () => {
        while (isAnimatingRef.current) {
          try {
            // On first cycle, pass callback to unblock spin after first line completes
            const unblockCallback = isFirstAnimationRef.current
              ? () => dispatchRef.current(setCounterAnimationFinished(true))
              : null;

            const playedAny = await cycleThroughLines(
              winningData,
              lineIds,
              isFirstAnimationRef.current,
              0,
              unblockCallback,
            );

            isFirstAnimationRef.current = false;
            currentLineIndexRef.current = 0;

            // A pass that played NOTHING ends the loop — see the flag's own
            // note. Looping forever is right when there is something to show;
            // with nothing to show there is no cycle, only a spin.
            if (!playedAny) {
              isAnimatingRef.current = false;
              break;
            }
          } catch (error) {
            // SAFETY NET: If the animation cycle errors out before onFirstLineComplete fired,
            // dispatch counterAnimationFinished(true) to prevent permanent spin button lockup.
            // Without this, the button stays blocked forever because winNotFullyDisplayed stays true.
            if (isFirstAnimationRef.current) {
              dispatchRef.current(setCounterAnimationFinished(true));
              isFirstAnimationRef.current = false;
            }
            break;
          }
        }
      };

      // FREE SPIN TRIGGER: Play lines linearly once (0 → scatter) and stop.
      // Scatter is always the last line, so we end on it naturally.
      if (
        FEATURES_CONFIG.FREE_SPINS.ENABLED &&
        store.getState().freeSpins.triggered
      ) {
        // Ensure animated win is tracked — for scatter-only rounds (4-5 scatters),
        // showAllWinningSymbolsOnce is skipped so winAnimationsPlaying was never set.
        // Without this, the scatter detection wait in cycleThroughLines can't catch it.
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, all payline wins were already displayed
        // simultaneously — skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          // SAFETY NET: Same as runSeamlessAnimationCycle — ensure spin button unblocks on error
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // ROYAL COLLECT TRIGGER: Play lines linearly once (0 → coin trigger) and stop.
      // Coin trigger is always the last line, so we end on it naturally — same as free spin scatters.
      if (
        FEATURES_CONFIG.ROYAL_COLLECT.ENABLED &&
        store.getState().featureGame.royalCollectTriggered
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // POT COLLECTION TRIGGER: Same pattern as RC — play lines linearly once.
      // if (
      //   FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
      //   store.getState().potCollection.potCollectionTriggered
      // ) {
      if (
        (FEATURES_CONFIG.POT_COLLECTION.ENABLED &&
          store.getState().potCollection.potCollectionTriggered) ||
        selectHoldAndWinIntroPending(store.getState()) // Hold & Win: same cycle (base game)
      ) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        const unblockCallback = isFirstAnimationRef.current
          ? () => dispatchRef.current(setCounterAnimationFinished(true))
          : null;

        // When show-all-wins is active, skip straight to the trigger line (always last).
        const triggerStartIndex = FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED
          ? lineIds.length - 1
          : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            unblockCallback,
          );
        } catch (error) {
          if (isFirstAnimationRef.current) {
            dispatchRef.current(setCounterAnimationFinished(true));
          }
        }
        isFirstAnimationRef.current = false;
        return;
      }

      // ── Cleopatra's Fire retrigger: the FREE SPIN TRIGGER's pattern ──
      //
      // Lines once, ending on the scatters, and stop — instead of the endless
      // cycle, whose first beat unblocks the round long before the scatters
      // have finished. When the last clip is done the popup may open (see
      // retriggerWinShown).
      if (store.getState().freeSpins.currentRound?.freeSpinsRetrigger > 0) {
        if (isAnimatedWin) {
          dispatchRef.current(setWinAnimationsPlaying(true));
        }

        // From the scatter line when show-all has already put the paylines
        // up — found, not assumed to be last.
        const scatterIndex = lineIds.findIndex(
          (id) => winningData[id]?.isScatter,
        );
        const triggerStartIndex =
          FEATURES_CONFIG.SHOW_ALL_WINS.ENABLED && scatterIndex > -1
            ? scatterIndex
            : 0;

        try {
          await cycleThroughLines(
            winningData,
            lineIds,
            isFirstAnimationRef.current,
            triggerStartIndex,
            null,
          );
        } catch (error) {
          console.error("[FreeSpins] retrigger win failed — releasing:", error);
        }
        isFirstAnimationRef.current = false;
        // The win is over only NOW — its last clip has played. The round's
        // loop moves on this; the popup opened on the intro's timing inside
        // cycleThroughLines, and the loop waits on it (unannouncedSpins).
        dispatchRef.current(setCounterAnimationFinished(true));
        // setRetriggerWinShown(true);
        return;
      }

      runSeamlessAnimationCycle();
    },
    [
      clearAllTimeouts,
      cycleThroughLines,
      dimReelSetRef,
      dispatch,
      globalLineWinTextRef,
      winType,
      isAnimatedWin,
    ],
  );

  const cycleThroughLinesRef = useRef(null);
  cycleThroughLinesRef.current = cycleThroughLines;

  const showAllWinningSymbolsOnceRef = useRef(null);
  showAllWinningSymbolsOnceRef.current = showAllWinningSymbolsOnce;



  const startWinningLinesAnimationRef = useRef(null);
  startWinningLinesAnimationRef.current = startWinningLinesAnimation;



  /**
   * Resets all animations and effects when a new spin starts.
   */
  useEffect(() => {
    if (bonusBoardActive) {
      return;
    }

    if (!isSpinning || !reelsetControllerRef.current) return;

    const wasAnimating = isAnimatingRef.current;

    // The room's held reaction goes with everything else the win put up —
    // see the playAccent call in the presentation. Mixed out, and only after
    // the pass that is running has finished, so the rat is never cut
    // mid-zap. See stopSceneAccents.
    freeSpinsGameViewControllerRef?.current?.stopAccent();

    // ── Synchronous visual cleanup ──────────────────────────────────

    // Must complete in the SAME frame before reels start spinning.
    // Previously resetAllEffects(true) was async (100ms undim fade),
    // which let reels begin while dim overlay was still fading.

    // 1. Snap dim overlay off instantly (no fade)
    if (dimReelSetRef.current?.snapClear) {
      dimReelSetRef.current.snapClear();
    }

    // 2. Hide win lines
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }

    // 3. Hide win boxes
    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }

    // 4. Hide overlay reel animations
    executeOnOverlayReelControllersRef.current((oc) => {
      if (oc?.hideAllAnimations) oc.hideAllAnimations();
      if (FEATURES_CONFIG.EXPANDING_WILDS.ENABLED && oc?.isExpandingWildVisible) {
        oc.forceStopExpandingWildFullReel?.();
      }
    });

    // 5. Restore symbol textures
    executeOnReelControllersRef.current((c) => {
      if (c?.showAllSymbolTextures) c.showAllSymbolTextures();
    });

    // 6. Hide line win text
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();
    // NOTE: globalLineWinTextRef is intentionally NOT hidden here.
    // It self-cleans via its spine "out" animation → _finalizeOut().
    // If a new win arrives, showWinAmount() replaces it seamlessly.

    // 7. Clear win position tracking
    prevWinPosSetRef.current = null;

    // ── End visual cleanup ──────────────────────────────────────────

    // Always clear all timeouts — catches orphaned timeouts from prior cycles
    clearAllTimeouts();

    if (wasAnimating) {
      isAnimatingRef.current = false;
    }

    dispatch(setWinAnimationsPlaying(false));
    dispatch(setIsAnimatedWin(false));
    dispatch(setCriticalAnimationPlaying(false));
    dispatch(setExpandingWildsOccurred(false));
    dispatch(setSkipWinAnimation(false));
    dispatch(setUserWantsToDismissWinAnimation(false));
    dispatch(setSkipCounterAnimation(false));
    dispatch(setIsWaitingForUserConfirmation(false));

    animationCompletedRef.current = false;
    fastPathRoundRef.current = null; // Clear fast path tag — new spin starts clean
    isFirstAnimationRef.current = true;
    animatedRoundsRef.current.clear();
    winSoundPlayedRef.current = false;
    expandingWildReelIndicesRef.current = [];
    expandingWildAnimationStateRef.current = {};
    winningDataRef.current = null;
    lineIdsRef.current = [];
    currentLineIndexRef.current = 0;

    useFastWinRef.current = false;

    soundManager.stop(SOUND_NAME.expanding_wild);
    if (FEATURES_CONFIG.WIN_LINES.ENABLED) {
      soundManager.stop(SOUND_NAME.win_line);
    }
  }, [
    isSpinning,

    freeSpinActive,
    bonusBoardActive,
    clearAllTimeouts,
    dispatch,
    dimReelSetRef,
    winLinesContainerRef,
    winBoxContainerRef,
    overlayReelRefs,
    globalLineWinTextRef,
    lineWinTextRef,
    // A context ref — a stable identity, so listing it satisfies the linter
    // without changing when this effect runs.
    freeSpinsGameViewControllerRef,
  ]);

  /**
   * STOP THE ROUND'S PRESENTATION when the base game takes the screen back.
   *
   * The full mirror of the base controller's own teardown, which has done this
   * from the start. This one used to stop the rat and nothing else, and that
   * gap is what carried a win from one free-spins game into the next.
   *
   * ── What actually happened, from the trace ──
   *
   * The last free spin of a round pays, the presentation starts, and then the
   * round runs OUT OF SPINS — the loop opens the total win dialog and the wipe
   * carries the player back to the base game while that presentation is still
   * in flight. Nothing cancelled it, so `isAnimatingRef` stayed true and its
   * timers kept going. Every following pass of the main effect reported
   * `BLOCKED by isAnimatingRef (already presenting)` — through the base spin,
   * through the trigger, into the NEW room — and the orphaned chain went on
   * painting the old round's boxes, dim and win text over the new game.
   *
   * The money was never the problem: on the reload trace every `money:` line
   * entering the new bonus reads zero. It was one round's presentation
   * outliving its round.
   *
   * A round that ends mid-presentation is normal — the outro is allowed to
   * interrupt — so the presentation has to be ended from the outside, and this
   * is the one event that means "this room is done": the base game owns the
   * screen.
   *
   * `animationCompletedRef` is deliberately NOT set here, exactly as the base
   * teardown leaves it: it gates the main effect, and setting it would block
   * the NEXT round's wins instead. Re-entry is already impossible — the wipe
   * nulls `currentRound` and the trigger replaces it with the arrival board,
   * which the main effect refuses on `played === 0`.
   */
  useEffect(() => {
    if (!bonusBoardActive) return;

    // The in-flight presentation, and its timers with it.
    if (isAnimatingRef.current) {
      isAnimatingRef.current = false;
      clearAllTimeouts();
    }

    // Everything it had put on the board.
    //
    // The BAKED textures first, and before resetAllEffects on purpose:
    // showAllSymbolTextures (which resetAllEffects calls) deliberately puts
    // every `_staticOverride` back, so restoring first would repaint the old
    // Golden Mystery coins rather than clear them.
    executeOnReelControllersRef.current?.((c) => c?.resetStaticOverrides?.());
    reelsetControllerRef.current?.resetAllEffects?.();
    if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
      winLinesContainerRef.current.hideWinLine();
    }
    if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
      winBoxContainerRef.current.hideWinBoxes();
    }
    if (globalLineWinTextRef?.current) globalLineWinTextRef.current.hide();
    if (lineWinTextRef?.current) lineWinTextRef.current.hide();

    // And the room's held reaction — stopped rather than cut, so the rat
    // finishes its pass instead of freezing mid-zap.
    freeSpinsGameViewControllerRef?.current?.stopAccent();

    fsDiag("winController -> base game owns the screen, room cycle stopped");
  }, [
    bonusBoardActive,
    clearAllTimeouts,
    // Context refs — stable identities, so listing them satisfies the linter
    // without changing when this effect runs.
    freeSpinsGameViewControllerRef,
    winLinesContainerRef,
    winBoxContainerRef,
    globalLineWinTextRef,
    lineWinTextRef,
  ]);

  /**
   * Resets visual effects when returning from free spins feature.
   */
  useEffect(() => {
    if (resetMainGameVisualEffects) {
      if (reelsetControllerRef.current?.reelControllers) {
        reelsetControllerRef.current.resetAllEffects();

        if (FEATURES_CONFIG.WIN_LINES.ENABLED && winLinesContainerRef.current) {
          winLinesContainerRef.current.hideWinLine();
        }

        if (FEATURES_CONFIG.WIN_BOX.ENABLED && winBoxContainerRef.current) {
          winBoxContainerRef.current.hideWinBoxes();
        }
      }

      if (isAnimatingRef.current) {
        isAnimatingRef.current = false;
        clearAllTimeouts();
      }

      animationCompletedRef.current = false;
      isFirstAnimationRef.current = true;
      animatedRoundsRef.current.clear();


      dispatch(setResetMainGameVisualEffects(false));
    }
  }, [
    resetMainGameVisualEffects,
    clearAllTimeouts,
    dispatch,
    winLinesContainerRef,
    winBoxContainerRef,
  ]);

  /**
   * Shows turbo suggestion popup on non-winning rounds when appropriate.
   */
  useEffect(() => {
    if (!FEATURES_CONFIG.TURBO_SUGGESTION.ENABLED) {
      return;
    }

    if (
      turboSuggestionPending &&
      spinComplete &&
      !isSpinning &&
      !freeSpinActive
    ) {
      if (!hasWins) {
        // Check if user previously declined — don't show again
        const uiSettings = StorageManager.loadUISettings();
        if (!uiSettings.turboSuggestionDeclined) {
          dispatch(openTurboSuggestionPopup());
        }
        dispatch(setTurboSuggestionPending(false));
      }
    }
  }, [
    turboSuggestionPending,
    spinComplete,
    isSpinning,
    freeSpinActive,
    bonusBoardActive,
    currentRound,
    dispatch,
    hasWins,
  ]);

  /**
   * Cleanup effect: Clears all timeouts and resets effects when component unmounts.
   */
  useEffect(() => {
    return () => {
      clearAllTimeouts();
      isAnimatingRef.current = false;

      if (reelsetControllerRef.current) {
        reelsetControllerRef.current.resetAllEffects();
      }
    };
  }, [clearAllTimeouts]);

  return {
    reelsetController: reelsetControllerRef.current,
  };
};

export default useFreeSpinBasicWinController;
